import React, { useState, useEffect } from 'react';
import {
    Stack,
    Text,
    TextField,
    Toggle,
    Dropdown,
    DefaultButton,
    PrimaryButton,
    MessageBar,
    MessageBarType,
    Spinner,
    SpinnerSize,
    Separator,
    Label,
    IStackTokens,
    mergeStyleSets,
    FontWeights,
    IDropdownOption,
    Checkbox,
    Pivot,
    PivotItem,
    Panel,
    PanelType,
    IconButton,
    TooltipHost,
    DirectionalHint,
    Dialog,
    DialogFooter,
    DialogType
} from '@fluentui/react';
import { authenticatedFetch } from '../utils/authApi';
import { 
    SettingsRegular,
    ServerRegular,
    DatabaseRegular,
    CloudRegular,
    ShieldRegular,
    AppGenericRegular,
    ColorRegular,
    SearchRegular,
    PersonRegular,
    BookRegular,
    CheckmarkCircleRegular,
    WarningRegular,
    InfoRegular,
    NewsRegular,
    PersonSettingsRegular,
    PlugConnectedRegular,
    LockClosedRegular,
    DeleteRegular,
    TemperatureRegular,
    BrainCircuitRegular,
    MoneyRegular,
    SlideTextRegular
} from '@fluentui/react-icons';
import ModelManagement from './AdminConfig/ModelManagement';
import { SettingsCard } from './AdminConfig/SettingsCard';
import { ConfigSectionRenderer } from './AdminConfig/ConfigSectionRenderer';
import DataSourceTiles from './admin/DataSourceTiles';
import AgentManagement from './admin/AgentManagement';
import StructuredDataSources from './admin/StructuredDataSources';
import FeaturePermissions from './admin/FeaturePermissions';
import TokenLimitsManagement from './admin/TokenLimitsManagement';
import HelpEditor from './HelpEditor/HelpEditor';
import AdminGuideEditor from './AdminGuideEditor/AdminGuideEditor';
import UIManagement from './UIManagement/UIManagement';
import NewsManagement from './News/NewsManagement';
import AdminLogoUploader from './AdminLogoUploader';
import AdminPresentationTemplates from './AdminPresentationTemplates';
import { EntraUserPicker, EntraGroupPicker } from './EntraUserPicker';
import styles from './AdminConfigPage.module.css';

interface ConfigField {
    key: string;
    label: string;
    field_type: string;
    default_value: any;
    current_value: any;
    description: string;
    placeholder: string;
    required: boolean;
    options: Array<{ value: any; label: string }>;
    validation_rules: any;
    sensitive: boolean;
    restart_required: boolean;
    // Dynamic select fields
    dynamic_source?: string;
    dynamic_value_field?: string;
    dynamic_label_field?: string;
}

interface ConfigSection {
    category: string;
    title: string;
    description: string;
    icon: string;
    fields: ConfigField[];
    collapsed: boolean;
    order: number;
}

interface AdminConfiguration {
    id: string;
    version: string;
    last_updated: string;
    updated_by: string;
    sections: ConfigSection[];
}

interface AdminSectionInfo {
    id: string;
    title: string;
    icon: JSX.Element;
    description: string;
    category: string;
}

const AdminConfigPage: React.FC = () => {
    const [configuration, setConfiguration] = useState<AdminConfiguration | null>(null);
    const [sensitiveKeysStatus, setSensitiveKeysStatus] = useState<Record<string, { has_value: boolean; source: string | null }>>({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ text: string; type: MessageBarType } | null>(null);
    const [selectedSection, setSelectedSection] = useState<string>('overview');
    const [isHelpEditorOpen, setIsHelpEditorOpen] = useState(false);
    const [isAdminGuideEditorOpen, setIsAdminGuideEditorOpen] = useState(false);
    const [cloudManagementInfo, setCloudManagementInfo] = useState<any>(null);
    const [loadingCloudInfo, setLoadingCloudInfo] = useState(false);
    const [editingCloudConfig, setEditingCloudConfig] = useState(false);
    const [cloudConfigChanges, setCloudConfigChanges] = useState<any>({});
    const [azureAgentValidationLoading, setAzureAgentValidationLoading] = useState<boolean>(false);
    const [azureAgentValidation, setAzureAgentValidation] = useState<any | null>(null);
    const [azureOpenAiValidationLoading, setAzureOpenAiValidationLoading] = useState<boolean>(false);
    const [azureOpenAiValidation, setAzureOpenAiValidation] = useState<any | null>(null);
    const [azureOpenAiTestQuery, setAzureOpenAiTestQuery] = useState<string>('Hello, can you help me with a simple question?');
    const [azureAgentTestQuery, setAzureAgentTestQuery] = useState<string>('Give me 2 tech news headlines from the last 24 hours and include clickable source URLs.');
    const [clearingOffice365Cache, setClearingOffice365Cache] = useState<boolean>(false);
    const [office365CacheMessage, setOffice365CacheMessage] = useState<string | null>(null);
    const [clearingRoutingFeedback, setClearingRoutingFeedback] = useState<boolean>(false);
    const [showClearRoutingDialog, setShowClearRoutingDialog] = useState<boolean>(false);
    const [showClearCostDialog, setShowClearCostDialog] = useState<boolean>(false);

    // Grounding instruction editor state
    const [showGroundingPanel, setShowGroundingPanel] = useState(false);
    const [editingGrounding, setEditingGrounding] = useState<string | null>(null);
    const [groundingText, setGroundingText] = useState('');
    const [editingGroundingLabel, setEditingGroundingLabel] = useState('');

    // Overview search filter state
    const [overviewSearchFilter, setOverviewSearchFilter] = useState('');

    // SharePoint credentials state
    const [sharePointCredentials, setSharePointCredentials] = useState({
        client_id: '',
        client_secret: '',
        tenant_id: ''
    });
    const [sharepointCredentialsLoading, setSharepointCredentialsLoading] = useState(false);
    const [testingCredentials, setTestingCredentials] = useState(false);

    // Helper function to get icon for a section based on its category/title
    const getIconForSection = (category: string, title: string): JSX.Element => {
        const lowercaseCategory = category.toLowerCase();
        const lowercaseTitle = title.toLowerCase();
        
        if (lowercaseCategory.includes('ai') || lowercaseTitle.includes('ai') || lowercaseTitle.includes('openai')) {
            return <AppGenericRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        if (lowercaseCategory.includes('database') || lowercaseTitle.includes('database') || lowercaseTitle.includes('cosmos')) {
            return <DatabaseRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        if (lowercaseCategory.includes('search') || lowercaseTitle.includes('search')) {
            return <SearchRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        if (lowercaseCategory.includes('auth') || lowercaseTitle.includes('auth') || lowercaseTitle.includes('security')) {
            return <ShieldRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        if (lowercaseCategory.includes('ui') || lowercaseTitle.includes('ui') || lowercaseTitle.includes('interface')) {
            return <ColorRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        if (lowercaseCategory.includes('integration') || lowercaseTitle.includes('integration') || lowercaseTitle.includes('office') || lowercaseTitle.includes('sharepoint')) {
            return <CloudRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        if (lowercaseCategory.includes('research') || lowercaseTitle.includes('research')) {
            return <BookRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        if (lowercaseCategory.includes('server') || lowercaseTitle.includes('server')) {
            return <ServerRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        if (lowercaseCategory.includes('user') || lowercaseTitle.includes('user')) {
            return <PersonRegular style={{ fontSize: 24, color: '#667eea' }} />;
        }
        
        // Default icon
        return <SettingsRegular style={{ fontSize: 24, color: '#667eea' }} />;
    };

    // Generate admin sections dynamically based on configuration
    const getAdminSections = (): AdminSectionInfo[] => {
        // Only keep essential hard-coded sections that are special components, not config-driven
        const sections: AdminSectionInfo[] = [
            {
                id: 'overview',
                title: 'System Overview',
                icon: <SettingsRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'System status and general information',
                category: 'overview'
            },
            {
                id: 'content-editors',
                title: 'Content Editors',
                icon: <BookRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'Edit user help and admin guide content',
                category: 'content-editors'
            },
            {
                id: 'ui-management',
                title: 'UI & Navigation Management',
                icon: <PersonSettingsRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'Manage navigation visibility and user access to pages',
                category: 'ui-management'
            },
            {
                id: 'news-management',
                title: 'News Management',
                icon: <NewsRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'Create and manage news articles and announcements',
                category: 'news-management'
            },
            {
                id: 'agent-management',
                title: 'AI Agent Management',
                icon: <BrainCircuitRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'Manage Azure AI Foundry agents with Bing grounding',
                category: 'agent-management'
            },
            {
                id: 'structured-data-sources',
                title: 'Structured Data Sources',
                icon: <DatabaseRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'Connect SQL databases with semantic column mappings',
                category: 'structured-data-sources'
            },
            {
                id: 'feature-permissions',
                title: 'Feature Permissions',
                icon: <LockClosedRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'Manage user and group access to specific features',
                category: 'feature-permissions'
            },
            {
                id: 'token-limits',
                title: 'Token Limits',
                icon: <MoneyRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'Configure token usage limits per user, group, and organization',
                category: 'token-limits'
            },
            {
                id: 'presentation-templates',
                title: 'Presentation Templates',
                icon: <SlideTextRegular style={{ fontSize: 24, color: '#667eea' }} />,
                description: 'Upload and manage PowerPoint templates for AI presentations',
                category: 'presentation-templates'
            }
            // All other sections come from Cosmos configuration only
        ];

        // Add all sections from Cosmos configuration
        if (configuration?.sections) {
            const existingIds = new Set(sections.map(s => s.id));
            configuration.sections.forEach(section => {
                // Skip if category id already exists to avoid duplicate entries
                if (!existingIds.has(section.category)) {
                    sections.push({
                        id: section.category,
                        title: section.title,
                        icon: getIconForSection(section.category, section.title),
                        description: section.description || `Configure ${section.title.toLowerCase()} settings`,
                        category: section.category
                    });
                    existingIds.add(section.category);
                }
            });
        }

        return sections;
    };

    useEffect(() => {
        loadConfiguration();
    }, []);

    useEffect(() => {
        if (selectedSection === 'cloud-management') {
            loadCloudManagementInfo();
        }
    }, [selectedSection]);

    const loadConfiguration = async () => {
        try {
            setLoading(true);
            const response = await authenticatedFetch('/api/admin/config');
            const result = await response.json();
            
            if (result.success) {
                setConfiguration(result.data);
                // Store sensitive keys status for credential field display
                if (result.sensitive_keys_status) {
                    setSensitiveKeysStatus(result.sensitive_keys_status);
                }
            } else {
                setMessage({ text: 'Failed to load configuration', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Error loading configuration', type: MessageBarType.error });
            console.error('Error loading configuration:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCloudManagementInfo = async () => {
        try {
            setLoadingCloudInfo(true);
            const response = await authenticatedFetch('/api/admin/cloud-management/info');
            const result = await response.json();
            
            if (result.success) {
                setCloudManagementInfo(result.cloud_management_info);
            } else {
                setMessage({ text: 'Failed to load cloud management info', type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Error loading cloud management info', type: MessageBarType.error });
            console.error('Error loading cloud management info:', error);
        } finally {
            setLoadingCloudInfo(false);
        }
    };

    const handleClearOffice365Cache = async () => {
        setClearingOffice365Cache(true);
        setOffice365CacheMessage(null);
        
        try {
            const response = await authenticatedFetch('/api/admin/clear-office365-cache', {
                method: 'POST',
            });
            
            const result = await response.json();
            
            if (result.success) {
                setOffice365CacheMessage('✅ Office 365 cache cleared successfully! Users will need to re-authenticate to get updated permissions.');
            } else {
                setOffice365CacheMessage(`❌ Failed to clear cache: ${result.message || 'Unknown error'}`);
            }
        } catch (error) {
            setOffice365CacheMessage(`❌ Error clearing cache: ${error}`);
        } finally {
            setClearingOffice365Cache(false);
        }
    };

    const updateFieldValue = (sectionCategory: string, fieldKey: string, value: any) => {
        if (!configuration) return;

        const updatedConfig = { ...configuration };
        const section = updatedConfig.sections.find(s => s.category === sectionCategory);
        if (section) {
            const field = section.fields.find(f => f.key === fieldKey);
            if (field) {
                field.current_value = value;
                setConfiguration(updatedConfig);
            }
        }
    };

    // Handle configuration button clicks (Clear FAQ Cache, Sync Schema, etc.)
    const handleConfigButtonClick = async (fieldKey: string) => {
        try {
            setMessage({ text: 'Action in progress...', type: MessageBarType.info });
            
            const response = await authenticatedFetch(`/api/admin/config/button/${fieldKey}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            const result = await response.json();
            
            if (result.success) {
                setMessage({ text: result.message, type: MessageBarType.success });
                
                // If there are details about removed fields, log them
                if (result.details?.removed_fields?.length > 0) {
                    console.log('Removed fields:', result.details.removed_fields);
                }
                
                // Refresh configuration after button action
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            } else {
                setMessage({ text: result.error || 'Action failed', type: MessageBarType.error });
            }
        } catch (error) {
            console.error('Button action error:', error);
            setMessage({ text: 'Error performing action', type: MessageBarType.error });
        }
    };

    const saveSection = async (sectionCategory: string) => {
        if (!configuration) return;

        const section = configuration.sections.find(s => s.category === sectionCategory);
        if (!section) return;

        try {
            setSaving(true);
            
            const fieldsToUpdate: { [key: string]: any } = {};
            section.fields.forEach(field => {
                fieldsToUpdate[field.key] = field.current_value ?? field.default_value;
            });

            const response = await authenticatedFetch(`/api/admin/config/sections/${sectionCategory}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    fields: fieldsToUpdate,
                    updated_by: 'admin_ui'
                }),
            });

            const result = await response.json();
            
            if (result.success) {
                setMessage({ text: `${section.title} saved successfully`, type: MessageBarType.success });
                await loadConfiguration();
            } else {
                setMessage({ text: `Failed to save ${section.title}`, type: MessageBarType.error });
            }
        } catch (error) {
            setMessage({ text: 'Error saving configuration', type: MessageBarType.error });
            console.error('Error saving section:', error);
        } finally {
            setSaving(false);
        }
    };

    // SharePoint credentials functions
    const loadSharePointCredentials = async () => {
        setSharepointCredentialsLoading(true);
        try {
            const response = await authenticatedFetch('/api/sharepoint-connector/credentials');
            const data = await response.json();
            
            if (data.success) {
                setSharePointCredentials({
                    client_id: data.credentials.client_id || '',
                    client_secret: data.credentials.client_secret || '',
                    tenant_id: data.credentials.tenant_id || ''
                });
            } else {
                console.warn('Failed to load SharePoint credentials:', data.error);
            }
        } catch (error) {
            console.error('Error loading SharePoint credentials:', error);
        } finally {
            setSharepointCredentialsLoading(false);
        }
    };

    const saveSharePointCredentials = async () => {
        setSharepointCredentialsLoading(true);
        try {
            const response = await authenticatedFetch('/api/sharepoint-connector/credentials', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(sharePointCredentials),
            });
            
            const data = await response.json();
            
            if (data.success) {
                setMessage({ 
                    text: 'SharePoint credentials saved successfully', 
                    type: MessageBarType.success 
                });
            } else {
                setMessage({ 
                    text: data.error || 'Failed to save SharePoint credentials', 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            console.error('Error saving SharePoint credentials:', error);
            setMessage({ 
                text: 'Error saving SharePoint credentials', 
                type: MessageBarType.error 
            });
        } finally {
            setSharepointCredentialsLoading(false);
        }
    };

    const testSharePointCredentials = async () => {
        setTestingCredentials(true);
        try {
            const response = await authenticatedFetch('/api/sharepoint-connector/credentials/test', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(sharePointCredentials),
            });
            
            const data = await response.json();
            
            if (data.success) {
                setMessage({ 
                    text: 'SharePoint credentials test successful', 
                    type: MessageBarType.success 
                });
            } else {
                setMessage({ 
                    text: data.error || 'SharePoint credentials test failed', 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            console.error('Error testing SharePoint credentials:', error);
            setMessage({ 
                text: 'Error testing SharePoint credentials', 
                type: MessageBarType.error 
            });
        } finally {
            setTestingCredentials(false);
        }
    };

    // Helper functions for grounding instruction editor
    const openGroundingEditor = (fieldKey: string, fieldLabel: string, currentValue: string) => {
        setEditingGrounding(fieldKey);
        setEditingGroundingLabel(fieldLabel);
        setGroundingText(currentValue || '');
        setShowGroundingPanel(true);
    };

    const saveGrounding = () => {
        if (editingGrounding && configuration) {
            const configSection = configuration.sections.find(s => s.category === 'hallucination_control');
            if (configSection) {
                updateFieldValue(configSection.category, editingGrounding, groundingText);
                setMessage({ text: 'Grounding instruction updated successfully', type: MessageBarType.success });
            }
        }
        setShowGroundingPanel(false);
        setEditingGrounding(null);
        setGroundingText('');
        setEditingGroundingLabel('');
    };

    const renderField = (field: ConfigField, sectionCategory: string) => {
        const value = field.current_value ?? field.default_value;
        const isSensitive = field.sensitive;

        // Hide legacy chat grounding fields (replaced by AI tile system)
        const legacyGroundingFields = [
            'CHAT_GROUNDING_MESSAGE', 'CHAT_GROUNDING_INDEXES', 'CHAT_GROUNDING_ENABLED',
            'grounding_message', 'grounding_indexes', 'grounding_enabled',
            'AZURE_SEARCH_INDEXES_JSON', 'AZURE_SEARCH_INDEXES_JSON_DEPRECATED'
        ];
        if (legacyGroundingFields.includes(field.key)) {
            return null;
        }

        // Replace raw path fields for UI logos with the uploader component
        if (sectionCategory === 'ui' && ['UI_LOGO', 'UI_FAVICON', 'UI_CHAT_LOGO'].includes(field.key)) {
            return (
                <div key={field.key} className={styles.fieldContainer}>
                    <AdminLogoUploader
                        fieldKey={field.key}
                        label={field.label}
                        onSaved={(url) => {
                            // update config state with new url
                            updateFieldValue(sectionCategory, field.key, url);
                            setMessage({ text: `${field.label} saved`, type: MessageBarType.success });
                        }}
                    />
                    {field.description && (
                        <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                            {field.description}
                        </Text>
                    )}
                </div>
            );
        }

        switch (field.field_type) {
            case 'boolean':
                // Avoid duplicating UI-specific toggles here; they are managed in the UI & Navigation Management page
                const uiToggleKeys = ['UI_SHOW_CHAT_HISTORY_BUTTON', 'show_chat_history_button'];
                if (sectionCategory === 'ui' && uiToggleKeys.includes(field.key)) {
                    return null;
                }

                return (
                    <div key={field.key} className={styles.fieldContainer}>
                        <Toggle
                            label={field.label}
                            checked={Boolean(value)}
                            onChange={(_, checked) => updateFieldValue(sectionCategory, field.key, checked)}
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );

            case 'dynamic_select':
                // For dynamic select, we need to get the options from the dynamic source
                const getDynamicOptions = () => {
                    if (field.dynamic_source && field.dynamic_source === 'MODEL_CONFIGURATIONS' && configuration) {
                        // Get model configurations from the current config
                        const modelConfigSection = configuration.sections.find(s => s.category === 'model_management');
                        if (modelConfigSection) {
                            const modelConfigField = modelConfigSection.fields.find(f => f.key === 'MODEL_CONFIGURATIONS');
                            if (modelConfigField && modelConfigField.current_value) {
                                try {
                                    const modelConfigs = typeof modelConfigField.current_value === 'string' 
                                        ? JSON.parse(modelConfigField.current_value) 
                                        : modelConfigField.current_value;
                                    if (Array.isArray(modelConfigs)) {
                                        return modelConfigs.map(config => ({
                                            key: config[field.dynamic_value_field || 'model_name'],
                                            text: config[field.dynamic_label_field || 'display_name'] || config[field.dynamic_value_field || 'model_name']
                                        }));
                                    }
                                } catch (e) {
                                    console.warn('Failed to parse model configurations for dynamic dropdown:', e);
                                }
                            }
                        }
                    }
                    // Fallback to static options if dynamic source fails
                    return field.options?.map(opt => ({
                        key: opt.value,
                        text: opt.label
                    })) || [];
                };
                
                const dynamicOptions = getDynamicOptions();
                
                return (
                    <div key={field.key} className={styles.fieldContainer}>
                        <Dropdown
                            label={field.label}
                            options={dynamicOptions}
                            selectedKey={value}
                            onChange={(_, option) => updateFieldValue(sectionCategory, field.key, option?.key)}
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );

            case 'select':
                const options: IDropdownOption[] = field.options.map(opt => ({
                    key: opt.value,
                    text: opt.label
                }));
                
                return (
                    <div key={field.key} className={styles.fieldContainer}>
                        <Dropdown
                            label={field.label}
                            options={options}
                            selectedKey={value}
                            onChange={(_, option) => updateFieldValue(sectionCategory, field.key, option?.key)}
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );

            case 'multiselect':
                const multiselectOptions: IDropdownOption[] = field.options.map(opt => ({
                    key: opt.value,
                    text: opt.label
                }));
                
                const selectedKeys = Array.isArray(value) ? value : [];
                
                return (
                    <div key={field.key} className={styles.fieldContainer}>
                        <Dropdown
                            label={field.label}
                            multiSelect
                            options={multiselectOptions}
                            selectedKeys={selectedKeys}
                            onChange={(_, option) => {
                                if (option) {
                                    const currentSelection = Array.isArray(value) ? [...value] : [];
                                    if (option.selected) {
                                        // Add the option if not already selected
                                        if (!currentSelection.includes(option.key as string)) {
                                            currentSelection.push(option.key as string);
                                        }
                                    } else {
                                        // Remove the option if it was deselected
                                        const index = currentSelection.indexOf(option.key as string);
                                        if (index > -1) {
                                            currentSelection.splice(index, 1);
                                        }
                                    }
                                    updateFieldValue(sectionCategory, field.key, currentSelection);
                                }
                            }}
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );

            case 'textarea':
                // Special handling for admin email/group fields - use Entra pickers
                if (field.key === 'ADMIN_EMAILS' || field.key === 'SUPER_ADMIN_EMAILS') {
                    // Convert comma-separated string to array for the picker
                    const emailArray = typeof value === 'string' 
                        ? value.split(',').map((e: string) => e.trim()).filter((e: string) => e.length > 0)
                        : (Array.isArray(value) ? value : []);
                    
                    return (
                        <div key={field.key} className={styles.fieldContainer}>
                            <EntraUserPicker
                                selectedEmails={emailArray}
                                onSelectionChange={(emails) => {
                                    // Store as comma-separated string to match backend expectation
                                    updateFieldValue(sectionCategory, field.key, emails.join(', '));
                                }}
                                label={field.label}
                                placeholder="Search users in Azure Entra..."
                                description={field.description}
                            />
                            {field.restart_required && (
                                <div className={styles.statusIndicator + ' ' + styles.warning}>
                                    <WarningRegular style={{ fontSize: 14 }} />
                                    Restart required after changing this setting
                                </div>
                            )}
                        </div>
                    );
                }
                
                if (field.key === 'ADMIN_GROUPS') {
                    // Convert comma-separated string to array for the picker
                    const groupArray = typeof value === 'string' 
                        ? value.split(',').map((g: string) => g.trim()).filter((g: string) => g.length > 0)
                        : (Array.isArray(value) ? value : []);
                    
                    return (
                        <div key={field.key} className={styles.fieldContainer}>
                            <EntraGroupPicker
                                selectedGroupIds={groupArray}
                                onSelectionChange={(groups) => {
                                    // Store as comma-separated string to match backend expectation
                                    updateFieldValue(sectionCategory, field.key, groups.join(', '));
                                }}
                                label={field.label}
                                placeholder="Search groups in Azure Entra..."
                                description={field.description}
                            />
                            {field.restart_required && (
                                <div className={styles.statusIndicator + ' ' + styles.warning}>
                                    <WarningRegular style={{ fontSize: 14 }} />
                                    Restart required after changing this setting
                                </div>
                            )}
                        </div>
                    );
                }
                
                // Special handling for Bing Custom Search System Message - add sync button
                if (field.key === 'BING_CUSTOM_SEARCH_SYSTEM_MESSAGE') {
                    return (
                        <div key={field.key} className={styles.fieldContainer}>
                            <TextField
                                label={field.label}
                                multiline
                                rows={10}
                                value={value || ''}
                                onChange={(_, newValue) => {
                                    updateFieldValue(sectionCategory, field.key, newValue);
                                }}
                                placeholder={field.placeholder}
                            />
                            {field.description && (
                                <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                    {field.description}
                                </Text>
                            )}
                            <Stack horizontal tokens={{ childrenGap: 8 }} style={{ marginTop: '12px' }}>
                                <PrimaryButton
                                    iconProps={{ iconName: 'Sync' }}
                                    text="Sync to All Custom Search Agents"
                                    onClick={async () => {
                                        try {
                                            setSaving(true);
                                            // First save the current value
                                            await saveSection(sectionCategory);
                                            // Then sync to all agents
                                            const response = await authenticatedFetch('/api/admin/agents/sync-custom-search-instructions', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ dry_run: false }),
                                            });
                                            const data = await response.json();
                                            if (data.success) {
                                                setMessage({ text: `✅ Synced to ${data.total_updated} agents. Updated: ${data.updated_agents?.join(', ') || 'none'}`, type: MessageBarType.success });
                                            } else {
                                                setMessage({ text: data.error || 'Failed to sync', type: MessageBarType.error });
                                            }
                                        } catch (err: any) {
                                            setMessage({ text: err.message || 'Failed to sync', type: MessageBarType.error });
                                        } finally {
                                            setSaving(false);
                                        }
                                    }}
                                    disabled={saving}
                                    styles={{ root: { backgroundColor: '#667eea' } }}
                                />
                                <DefaultButton
                                    iconProps={{ iconName: 'View' }}
                                    text="Preview (Dry Run)"
                                    onClick={async () => {
                                        try {
                                            const response = await authenticatedFetch('/api/admin/agents/sync-custom-search-instructions', {
                                                method: 'POST',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ dry_run: true }),
                                            });
                                            const data = await response.json();
                                            if (data.success) {
                                                setMessage({ text: `Preview: Would update ${data.total_updated} agents (${data.updated_agents?.join(', ') || 'none'}). Skipped: ${data.skipped_agents?.length || 0}`, type: MessageBarType.info });
                                            } else {
                                                setMessage({ text: data.error || 'Failed to preview', type: MessageBarType.error });
                                            }
                                        } catch (err: any) {
                                            setMessage({ text: err.message || 'Failed to preview', type: MessageBarType.error });
                                        }
                                    }}
                                    disabled={saving}
                                />
                            </Stack>
                            {field.restart_required && (
                                <div className={styles.statusIndicator + ' ' + styles.warning}>
                                    <WarningRegular style={{ fontSize: 14 }} />
                                    Restart required after changing this setting
                                </div>
                            )}
                        </div>
                    );
                }
                
                return (
                    <div key={field.key} className={isSensitive ? styles.sensitiveField : styles.fieldContainer}>
                        <TextField
                            label={field.label}
                            multiline
                            rows={4}
                            value={value || ''}
                            onChange={(_, newValue) => updateFieldValue(sectionCategory, field.key, newValue)}
                            placeholder={field.placeholder}
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );

            case 'password':
                return (
                    <div key={field.key} className={styles.sensitiveField}>
                        <TextField
                            label={field.label}
                            type="password"
                            value={value || ''}
                            onChange={(_, newValue) => updateFieldValue(sectionCategory, field.key, newValue)}
                            placeholder={field.placeholder}
                            canRevealPassword
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );

            case 'number':
            case 'float':
                // Extract validation rules for better input constraints
                const validation = field.validation_rules || {};
                const minValue = validation.min !== undefined ? validation.min : undefined;
                const maxValue = validation.max !== undefined ? validation.max : undefined;
                const stepValue = validation.step !== undefined ? validation.step : (field.field_type === 'float' ? 0.01 : 1);
                
                return (
                    <div key={field.key} className={styles.fieldContainer}>
                        <TextField
                            label={field.label}
                            type="number"
                            value={value?.toString() || ''}
                            onChange={(_, newValue) => {
                                console.log(`Updating ${field.key}: ${newValue} (type: ${field.field_type}, step: ${stepValue})`);
                                // Special handling for cascade threshold field and other confidence/probability fields
                                const isFloatField = field.field_type === 'float' || 
                                                   stepValue === 0.01 || stepValue < 1 ||
                                                   field.key === 'PRIORITY_CASCADE_CONFIDENCE_THRESHOLD' ||
                                                   field.key.toLowerCase().includes('threshold') ||
                                                   field.key.toLowerCase().includes('confidence') ||
                                                   (minValue !== undefined && minValue < 1 && maxValue !== undefined && maxValue <= 1);
                                const val = isFloatField ? parseFloat(newValue || '0') : parseInt(newValue || '0');
                                console.log(`Parsed value: ${val} (using ${isFloatField ? 'parseFloat' : 'parseInt'}, isFloatField: ${isFloatField})`);
                                updateFieldValue(sectionCategory, field.key, val);
                            }}
                            placeholder={field.placeholder}
                            min={minValue}
                            max={maxValue}
                            step={stepValue}
                            inputMode={field.field_type === 'float' ? 'decimal' : 'numeric'}
                            styles={{
                                root: { maxWidth: '150px' }
                            }}
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                                {(minValue !== undefined || maxValue !== undefined) && (
                                    <span style={{ color: '#667eea', marginLeft: '4px' }}>
                                        ({minValue !== undefined ? `min: ${minValue}` : ''}{minValue !== undefined && maxValue !== undefined ? ', ' : ''}{maxValue !== undefined ? `max: ${maxValue}` : ''})
                                    </span>
                                )}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );

            case 'json':
                        // Prevent chat grounding message/index fields from appearing (replaced by AI tile system)
                        if (legacyGroundingFields.includes(field.key)) {
                            return null;
                        }

                        // Special handling for model configurations
                if (field.key === 'MODEL_CONFIGURATIONS') {
                    const modelConfigs = (() => {
                        try {
                            return typeof value === 'string' ? JSON.parse(value) : Array.isArray(value) ? value : [];
                        } catch {
                            return [];
                        }
                    })();

                    return (
                        <div key={field.key} className={styles.fieldContainer}>
                            <ModelManagement
                                value={modelConfigs}
                                onChange={(models) => updateFieldValue(sectionCategory, field.key, JSON.stringify(models, null, 2))}
                            />
                            {field.description && (
                                <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                    {field.description}
                                </Text>
                            )}
                            {field.restart_required && (
                                <div className={styles.statusIndicator + ' ' + styles.warning}>
                                    <WarningRegular style={{ fontSize: 14 }} />
                                    Restart required after changing this setting
                                </div>
                            )}
                        </div>
                    );
                }
                
                // Default JSON handling for other fields
                return (
                    <div key={field.key} className={styles.fieldContainer}>
                        <TextField
                            label={field.label}
                            multiline
                            rows={6}
                            value={JSON.stringify(value, null, 2) || ''}
                            onChange={(_, newValue) => {
                                try {
                                    const jsonValue = JSON.parse(newValue || '{}');
                                    updateFieldValue(sectionCategory, field.key, jsonValue);
                                } catch {
                                    // Invalid JSON, don't update
                                }
                            }}
                            placeholder={field.placeholder}
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );

            case 'button':
                return (
                    <div key={field.key} className={styles.fieldContainer}>
                        <Stack tokens={{ childrenGap: 8 }}>
                            <Text variant="medium" style={{ fontWeight: 600 }}>
                                {field.label}
                            </Text>
                            <PrimaryButton
                                text={field.placeholder || field.label}
                                onClick={async () => {
                                    try {
                                        setMessage({ text: `${field.label} in progress...`, type: MessageBarType.info });
                                        
                                        const response = await authenticatedFetch(`/api/admin/config/button/${field.key}`, {
                                            method: 'POST',
                                            headers: {
                                                'Content-Type': 'application/json'
                                            }
                                        });
                                        
                                        const result = await response.json();
                                        
                                        if (result.success) {
                                            setMessage({ text: result.message, type: MessageBarType.success });
                                            
                                            // If there are details about removed fields, log them
                                            if (result.details && result.details.removed_fields && result.details.removed_fields.length > 0) {
                                                console.log('Removed fields:', result.details.removed_fields);
                                            }
                                            
                                            // Refresh configuration after button action
                                            setTimeout(() => {
                                                window.location.reload();
                                            }, 2000);
                                        } else {
                                            setMessage({ text: result.error || 'Button action failed', type: MessageBarType.error });
                                        }
                                    } catch (error) {
                                        console.error('Button action error:', error);
                                        setMessage({ text: 'Error performing button action', type: MessageBarType.error });
                                    }
                                }}
                            />
                            {field.description && (
                                <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                    {field.description}
                                </Text>
                            )}
                        </Stack>
                    </div>
                );

            case 'info':
                // Info-only field - displays label with info badge and tooltip, no editable input
                return (
                    <div key={field.key} className={styles.fieldContainer}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                            <Text variant="medium" style={{ fontWeight: 600 }}>
                                {field.label}
                            </Text>
                            <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 10px',
                                borderRadius: '4px',
                                backgroundColor: '#e1f5fe',
                                color: '#0277bd',
                                fontSize: '12px',
                                fontWeight: 500,
                                border: '1px solid #81d4fa'
                            }}>
                                🔐 {field.default_value || 'Read-only'}
                            </span>
                            <TooltipHost
                                content={
                                    <div style={{ padding: '12px', maxWidth: '350px', whiteSpace: 'pre-line' }}>
                                        <Text variant="small" style={{ color: '#323130' }}>
                                            {field.description}
                                        </Text>
                                    </div>
                                }
                                directionalHint={DirectionalHint.rightCenter}
                            >
                                <InfoRegular style={{ fontSize: 18, color: '#667eea', cursor: 'help' }} />
                            </TooltipHost>
                        </Stack>
                    </div>
                );

            default:
                return (
                    <div key={field.key} className={isSensitive ? styles.sensitiveField : styles.fieldContainer}>
                        <TextField
                            label={field.label}
                            value={value || ''}
                            onChange={(_, newValue) => updateFieldValue(sectionCategory, field.key, newValue)}
                            placeholder={field.placeholder}
                            type={field.field_type === 'password' ? 'password' : 'text'}
                        />
                        {field.description && (
                            <Text variant="small" style={{ color: '#666', marginTop: '4px' }}>
                                {field.description}
                            </Text>
                        )}
                        {field.restart_required && (
                            <div className={styles.statusIndicator + ' ' + styles.warning}>
                                <WarningRegular style={{ fontSize: 14 }} />
                                Restart required after changing this setting
                            </div>
                        )}
                    </div>
                );
        }
    };

    const renderOverview = () => {
        if (!configuration) return null;

        // Get all admin sections for the overview
        const allAdminSections = getAdminSections();
        
        // Filter sections based on search
        const filteredSections = allAdminSections.filter(section => {
            if (!overviewSearchFilter.trim()) return true;
            const searchLower = overviewSearchFilter.toLowerCase();
            return (
                section.title.toLowerCase().includes(searchLower) ||
                section.description.toLowerCase().includes(searchLower) ||
                section.id.toLowerCase().includes(searchLower)
            );
        });

        return (
            <Stack tokens={{ childrenGap: 24 }}>
                {/* Stats Cards */}
                <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                    {/* System Status Card */}
                    <div style={{
                        flex: 2,
                        minWidth: 280,
                        padding: 24,
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 16,
                        color: 'white',
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)'
                    }}>
                        <Stack horizontal horizontalAlign="space-between" verticalAlign="start">
                            <div>
                                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 8 }}>
                                    <CheckmarkCircleRegular style={{ fontSize: 20 }} />
                                    <Text style={{ color: 'rgba(255,255,255,0.9)', fontSize: 14 }}>System Status</Text>
                                </Stack>
                                <Text style={{ fontSize: 28, fontWeight: 700, color: 'white', display: 'block' }}>
                                    Active & Healthy
                                </Text>
                                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, marginTop: 4 }}>
                                    Version {configuration.version}
                                </Text>
                            </div>
                            <ServerRegular style={{ fontSize: 40, opacity: 0.3 }} />
                        </Stack>
                    </div>

                    {/* Sections Count */}
                    <div style={{
                        padding: 24,
                        background: 'white',
                        borderRadius: 16,
                        border: '1px solid #e5e7eb',
                        minWidth: 140,
                        textAlign: 'center',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                    }}>
                        <div style={{
                            display: 'inline-flex',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: 10,
                            padding: 10,
                            marginBottom: 12
                        }}>
                            <SettingsRegular style={{ fontSize: 20, color: 'white' }} />
                        </div>
                        <Text style={{ fontSize: 28, fontWeight: 700, color: '#1a253c', display: 'block' }}>
                            {allAdminSections.length}
                        </Text>
                        <Text variant="small" style={{ color: '#6b7280' }}>Admin Sections</Text>
                    </div>

                    {/* Last Updated */}
                    <div style={{
                        padding: 24,
                        background: 'white',
                        borderRadius: 16,
                        border: '1px solid #e5e7eb',
                        minWidth: 180,
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                    }}>
                        <div style={{
                            display: 'inline-flex',
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            borderRadius: 10,
                            padding: 10,
                            marginBottom: 12
                        }}>
                            <InfoRegular style={{ fontSize: 20, color: 'white' }} />
                        </div>
                        <Text style={{ fontSize: 14, fontWeight: 600, color: '#1a253c', display: 'block' }}>
                            Last Updated
                        </Text>
                        <Text style={{ fontSize: 13, color: '#6b7280', display: 'block', marginTop: 4 }}>
                            {new Date(configuration.last_updated).toLocaleDateString()}
                        </Text>
                        <Text variant="tiny" style={{ color: '#9ca3af' }}>
                            by {configuration.updated_by}
                        </Text>
                    </div>
                </Stack>

                {/* All Admin Sections with Search */}
                <div style={{
                    background: 'white',
                    borderRadius: 16,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                }}>
                    {/* Card Header */}
                    <div style={{
                        background: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        padding: '16px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #5a4f9e 100%)',
                                borderRadius: 8,
                                padding: 8,
                                display: 'flex'
                            }}>
                                <DatabaseRegular style={{ fontSize: 18, color: 'white' }} />
                            </div>
                            <Text variant="large" style={{ fontWeight: 600, color: '#1a253c', margin: 0 }}>
                                All Admin Sections
                            </Text>
                            <span style={{ 
                                fontSize: 12, 
                                fontWeight: 500, 
                                color: '#667eea',
                                background: '#ede9fe',
                                padding: '4px 12px',
                                borderRadius: 12
                            }}>
                                {filteredSections.length} of {allAdminSections.length}
                            </span>
                        </Stack>
                        <TextField
                            placeholder="Search sections..."
                            value={overviewSearchFilter}
                            onChange={(_, value) => setOverviewSearchFilter(value || '')}
                            iconProps={{ iconName: 'Search' }}
                            styles={{
                                root: { width: 250 },
                                field: { fontSize: 13 }
                            }}
                        />
                    </div>
                    
                    {/* Card Body */}
                    <div style={{ padding: 24 }}>
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                            gap: 12
                        }}>
                            {filteredSections.map(section => (
                                <div 
                                    key={section.id}
                                    onClick={() => setSelectedSection(section.id)}
                                style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 12,
                                    padding: '14px 16px',
                                    background: '#f9fafb',
                                    borderRadius: 8,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    border: '1px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#e3f2fd';
                                    e.currentTarget.style.borderColor = '#667eea';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#f9fafb';
                                    e.currentTarget.style.borderColor = 'transparent';
                                }}
                            >
                                <div style={{ flexShrink: 0 }}>{section.icon}</div>
                                <Stack tokens={{ childrenGap: 2 }} style={{ overflow: 'hidden', minWidth: 0 }}>
                                    <Text style={{ fontSize: 14, fontWeight: 500, color: '#374151' }}>
                                        {section.title}
                                    </Text>
                                    <Text style={{ 
                                        fontSize: 12, 
                                        color: '#6b7280',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {section.description}
                                    </Text>
                                </Stack>
                            </div>
                        ))}
                        </div>
                    </div>
                    
                    {filteredSections.length === 0 && (
                        <div style={{ 
                            textAlign: 'center', 
                            padding: '40px 20px',
                            color: '#6b7280'
                        }}>
                            <SearchRegular style={{ fontSize: 32, marginBottom: 12, opacity: 0.5 }} />
                            <Text style={{ display: 'block', fontSize: 14 }}>
                                No sections found matching "{overviewSearchFilter}"
                            </Text>
                        </div>
                    )}
                </div>

                {/* Action Buttons Card */}
                <div style={{
                    background: 'white',
                    borderRadius: 16,
                    border: '1px solid #e5e7eb',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                }}>
                    {/* Card Header */}
                    <div style={{
                        background: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        padding: '16px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #5a4f9e 100%)',
                            borderRadius: 8,
                            padding: 8,
                            display: 'flex'
                        }}>
                            <AppGenericRegular style={{ fontSize: 18, color: 'white' }} />
                        </div>
                        <Text variant="large" style={{ fontWeight: 600, color: '#1a253c', margin: 0 }}>
                            Quick Actions
                        </Text>
                    </div>
                    {/* Card Body */}
                    <div style={{ padding: 24 }}>
                        <Stack horizontal tokens={{ childrenGap: 12 }} wrap>
                            <PrimaryButton
                                text="Refresh Configuration"
                                iconProps={{ iconName: 'Refresh' }}
                                onClick={loadConfiguration}
                                disabled={loading}
                                styles={{
                                    root: { 
                                        borderRadius: 8,
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        border: 'none'
                                    },
                                    rootHovered: {
                                        background: 'linear-gradient(135deg, #5a4f9e 0%, #764ba2 100%)'
                                    }
                                }}
                            />
                            <DefaultButton
                                text="Clear Cost Data"
                                iconProps={{ iconName: 'Delete' }}
                                onClick={() => setShowClearCostDialog(true)}
                                disabled={saving}
                                styles={{
                                    root: { 
                                        borderRadius: 8,
                                        borderColor: '#d1d5db'
                                    },
                                    rootHovered: {
                                        borderColor: '#667eea',
                                        color: '#667eea'
                                    }
                                }}
                            />
                            <DefaultButton
                                text={clearingRoutingFeedback ? "Clearing..." : "Clear Routing Feedback"}
                                iconProps={{ iconName: 'Brain' }}
                                onClick={() => setShowClearRoutingDialog(true)}
                                disabled={clearingRoutingFeedback}
                                styles={{
                                    root: { 
                                        borderRadius: 8,
                                        borderColor: '#d1d5db'
                                    },
                                    rootHovered: {
                                        borderColor: '#667eea',
                                        color: '#667eea'
                                    }
                                }}
                            />
                        </Stack>
                    </div>

                    {/* Clear Cost Data Confirmation Dialog */}
                    <Dialog
                        hidden={!showClearCostDialog}
                        onDismiss={() => setShowClearCostDialog(false)}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: '⚠️ Clear Cost Data',
                            subText: 'This will permanently delete ALL cost tracking data. This action cannot be undone.'
                        }}
                        modalProps={{
                            isBlocking: true,
                            styles: { main: { maxWidth: 450 } }
                        }}
                    >
                        <Stack tokens={{ childrenGap: 12 }} style={{ padding: '0 0 16px 0' }}>
                            <MessageBar messageBarType={MessageBarType.severeWarning}>
                                All historical cost records, usage statistics, and billing data will be permanently deleted.
                            </MessageBar>
                        </Stack>
                        <DialogFooter>
                            <DefaultButton 
                                onClick={() => setShowClearCostDialog(false)} 
                                text="Cancel" 
                            />
                            <PrimaryButton 
                                onClick={async () => {
                                    setShowClearCostDialog(false);
                                    try {
                                        setSaving(true);
                                        const response = await authenticatedFetch('/api/cost/clear', {
                                            method: 'DELETE'
                                        });
                                        const result = await response.json();
                                        
                                        if (result.success) {
                                            setMessage({ 
                                                text: `Successfully cleared ${result.deleted_count} cost tracking records`, 
                                                type: MessageBarType.success 
                                            });
                                        } else {
                                            setMessage({ 
                                                text: result.error || 'Failed to clear cost data', 
                                                type: MessageBarType.error 
                                            });
                                        }
                                    } catch (error) {
                                        console.error('Clear cost data failed:', error);
                                        setMessage({ 
                                            text: 'Error clearing cost data', 
                                            type: MessageBarType.error 
                                        });
                                    } finally {
                                        setSaving(false);
                                    }
                                }} 
                                text="Delete All Cost Data"
                                styles={{
                                    root: { backgroundColor: '#d13438' },
                                    rootHovered: { backgroundColor: '#a4262c' }
                                }}
                            />
                        </DialogFooter>
                    </Dialog>

                    {/* Clear Routing Feedback Confirmation Dialog */}
                    <Dialog
                        hidden={!showClearRoutingDialog}
                        onDismiss={() => setShowClearRoutingDialog(false)}
                        dialogContentProps={{
                            type: DialogType.normal,
                            title: '🧠 Clear Learned Routing Patterns',
                            subText: 'This will clear all learned routing patterns from user interactions.'
                        }}
                        modalProps={{
                            isBlocking: true,
                            styles: { main: { maxWidth: 450 } }
                        }}
                    >
                        <Stack tokens={{ childrenGap: 12 }} style={{ padding: '0 0 16px 0' }}>
                            <MessageBar messageBarType={MessageBarType.warning}>
                                The system learns from user source selections to improve routing for vague queries. 
                                After clearing, the system will need to re-learn user preferences.
                            </MessageBar>
                            <Text variant="small" style={{ color: '#605e5c' }}>
                                This is useful when you want to reset learned patterns after significant data source changes.
                            </Text>
                        </Stack>
                        <DialogFooter>
                            <DefaultButton 
                                onClick={() => setShowClearRoutingDialog(false)} 
                                text="Cancel" 
                            />
                            <PrimaryButton 
                                onClick={async () => {
                                    setShowClearRoutingDialog(false);
                                    try {
                                        setClearingRoutingFeedback(true);
                                        const response = await authenticatedFetch('/api/admin/routing-feedback/clear', {
                                            method: 'POST'
                                        });
                                        const result = await response.json();
                                        
                                        if (result.success) {
                                            setMessage({ 
                                                text: `Cleared ${result.deleted_count} routing feedback patterns`, 
                                                type: MessageBarType.success 
                                            });
                                        } else {
                                            setMessage({ 
                                                text: result.error || 'Failed to clear routing feedback', 
                                                type: MessageBarType.error 
                                            });
                                        }
                                    } catch (error) {
                                        console.error('Clear routing feedback failed:', error);
                                        setMessage({ 
                                            text: 'Error clearing routing feedback', 
                                            type: MessageBarType.error 
                                        });
                                    } finally {
                                        setClearingRoutingFeedback(false);
                                    }
                                }} 
                                text="Clear Routing Data"
                            />
                        </DialogFooter>
                    </Dialog>
                </div>
            </Stack>
        );
    };

    const renderConfigSection = (sectionInfo: AdminSectionInfo) => {
        if (!configuration) return null;

        const configSection = configuration.sections.find(s => s.category === sectionInfo.category);
        if (!configSection) {
            return (
                <Stack tokens={{ childrenGap: 20 }}>
                    <Text variant="large" style={{ fontWeight: 600, color: '#1a253c' }}>
                        {sectionInfo.title}
                    </Text>
                    <MessageBar messageBarType={MessageBarType.warning}>
                        This configuration section is not available in the current system configuration.
                    </MessageBar>
                </Stack>
            );
        }

        // Special modern design for intent_detection
        if (configSection.category === 'intent_detection') {
            return (
                <Stack tokens={{ childrenGap: 24 }}>
                    {/* Modern Header Banner */}
                    <div style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: 16,
                        padding: '32px 28px',
                        color: 'white',
                        boxShadow: '0 4px 20px rgba(0, 120, 212, 0.25)'
                    }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }}>
                            <div style={{
                                background: 'rgba(255, 255, 255, 0.15)',
                                borderRadius: 12,
                                padding: 14,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <BrainCircuitRegular style={{ fontSize: 28, color: 'white' }} />
                            </div>
                            <Stack tokens={{ childrenGap: 4 }}>
                                <Text variant="xLarge" style={{ fontWeight: 700, color: 'white', margin: 0 }}>
                                    {configSection.title}
                                </Text>
                                <Text variant="medium" style={{ color: 'rgba(255, 255, 255, 0.85)', margin: 0 }}>
                                    {configSection.description}
                                </Text>
                            </Stack>
                        </Stack>
                    </div>
                    
                    {/* Settings Card */}
                    <div style={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: 16,
                        padding: 0,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                    }}>
                        {/* Card Header */}
                        <div style={{
                            background: '#f8fafc',
                            borderBottom: '1px solid #e5e7eb',
                            padding: '16px 24px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12
                        }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #5a4f9e 100%)',
                                borderRadius: 8,
                                padding: 8,
                                display: 'flex'
                            }}>
                                <SettingsRegular style={{ fontSize: 18, color: 'white' }} />
                            </div>
                            <Text variant="large" style={{ fontWeight: 600, color: '#1a253c', margin: 0 }}>
                                Detection Settings
                            </Text>
                        </div>
                        {/* Card Body */}
                        <div style={{ padding: 24 }}>
                            <Stack tokens={{ childrenGap: 16 }}>
                                <Stack tokens={{ childrenGap: 12 }}>
                                    {configSection.fields
                                        .filter(field => ['SHOW_INTENT_REASONING', 'ENABLE_PRIORITY_CASCADE', 'PRIORITY_CASCADE_CONFIDENCE_THRESHOLD', 'DYNAMIC_SYSTEM_MESSAGE_ENABLED'].includes(field.key))
                                        .map((field) => renderField(field, configSection.category))}
                                </Stack>
                                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 8 }}>
                                    <Stack horizontal tokens={{ childrenGap: 12 }}>
                                        <PrimaryButton
                                            text={saving ? "Saving..." : "Save Changes"}
                                            onClick={() => saveSection(configSection.category)}
                                            disabled={saving}
                                            styles={{
                                                root: {
                                                    borderRadius: 8,
                                                    padding: '8px 20px',
                                                    height: 40,
                                                    background: 'linear-gradient(135deg, #667eea 0%, #5a4f9e 100%)',
                                                    border: 'none'
                                                },
                                                rootHovered: {
                                                    background: 'linear-gradient(135deg, #5a4f9e 0%, #764ba2 100%)'
                                                }
                                            }}
                                        />
                                        <DefaultButton
                                            text="Reset to Defaults"
                                            styles={{
                                                root: {
                                                    borderRadius: 8,
                                                    padding: '8px 20px',
                                                    height: 40,
                                                    borderColor: '#d1d5db'
                                                },
                                                rootHovered: {
                                                    borderColor: '#667eea',
                                                    color: '#667eea'
                                                }
                                            }}
                                            onClick={() => {
                                                const updatedConfig = { ...configuration };
                                                const section = updatedConfig.sections.find(s => s.category === configSection.category);
                                                if (section) {
                                                    section.fields.forEach(field => {
                                                        field.current_value = field.default_value;
                                                    });
                                                    setConfiguration(updatedConfig);
                                                }
                                            }}
                                        />
                                    </Stack>
                                </div>
                            </Stack>
                        </div>
                    </div>
                    
                    {/* Data Sources Card */}
                    <div style={{
                        background: 'white',
                        border: '1px solid #e5e7eb',
                        borderRadius: 16,
                        padding: 0,
                        overflow: 'hidden',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                    }}>
                        {/* Card Header */}
                        <div style={{
                            background: '#f8fafc',
                            borderBottom: '1px solid #e5e7eb',
                            padding: '16px 24px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12
                        }}>
                            <div style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #5a4f9e 100%)',
                                borderRadius: 8,
                                padding: 8,
                                display: 'flex'
                            }}>
                                <DatabaseRegular style={{ fontSize: 18, color: 'white' }} />
                            </div>
                            <Text variant="large" style={{ fontWeight: 600, color: '#1a253c', margin: 0 }}>
                                Data Sources
                            </Text>
                        </div>
                        {/* Card Body */}
                        <div style={{ padding: 24 }}>
                            <DataSourceTiles />
                        </div>
                    </div>
                </Stack>
            );
        }

        // Helper to render Azure Agent validation controls
        const renderAzureAgentValidation = () => {
            const runValidation = async () => {
                try {
                    setAzureAgentValidationLoading(true);
                    setAzureAgentValidation(null);
                    const resp = await authenticatedFetch('/api/admin/azure-agent/validate', { method: 'GET' });
                    const data = await resp.json();
                    setAzureAgentValidation(data);
                } catch (e) {
                    setAzureAgentValidation({ success: false, error: (e as any)?.message || 'Validation failed' });
                } finally {
                    setAzureAgentValidationLoading(false);
                }
            };

            const runLiveTest = async (mode: 'connectivity' | 'full') => {
                try {
                    setAzureAgentValidationLoading(true);
                    const resp = await authenticatedFetch('/api/admin/azure-agent/test', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ mode, query: mode === 'full' ? azureAgentTestQuery : undefined })
                    });
                    const data = await resp.json();
                    setAzureAgentValidation({
                        success: data.success,
                        data: data.data,
                        error: data.error
                    });
                } catch (e) {
                    setAzureAgentValidation({ success: false, error: (e as any)?.message || 'Live test failed' });
                } finally {
                    setAzureAgentValidationLoading(false);
                }
            };

            return (
                <Stack tokens={{ childrenGap: 10 }}>
                    <Separator />
                    <Text variant="medium" style={{ fontWeight: 600 }}>Azure Agent Validation</Text>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text={azureAgentValidationLoading ? 'Validating…' : 'Validate Azure Agent'} disabled={azureAgentValidationLoading} onClick={runValidation} />
                        <DefaultButton text={azureAgentValidationLoading ? 'Testing…' : 'Test Connectivity'} disabled={azureAgentValidationLoading} onClick={() => runLiveTest('connectivity')} />
                        <DefaultButton text={azureAgentValidationLoading ? 'Testing…' : 'Run Full Test'} disabled={azureAgentValidationLoading} onClick={() => runLiveTest('full')} />
                        {azureAgentValidation && (
                            <MessageBar messageBarType={azureAgentValidation.success ? MessageBarType.success : MessageBarType.error} isMultiline>
                                {azureAgentValidation.success ? 'Azure Agent configuration looks good.' : 'Azure Agent validation found issues.'}
                            </MessageBar>
                        )}
                    </Stack>
                    {/* Custom full-test query */}
                    <Stack tokens={{ childrenGap: 4 }} style={{ maxWidth: 700 }}>
                        <Label>Full test query</Label>
                        <TextField
                            placeholder="Enter a query that requires a web search and ask for sources"
                            value={azureAgentTestQuery}
                            onChange={(_, v) => setAzureAgentTestQuery(v || '')}
                        />
                    </Stack>
                    {azureAgentValidation?.data && (
                        <Stack tokens={{ childrenGap: 6 }} style={{ background: '#faf9f8', padding: 12, borderRadius: 4 }}>
                            <Text variant="smallPlus" style={{ fontWeight: 600 }}>Diagnostics</Text>
                            {typeof azureAgentValidation.data.sdk_available !== 'undefined' && (
                                <Text variant="small">SDK available: {String(azureAgentValidation.data.sdk_available)}</Text>
                            )}
                            {typeof azureAgentValidation.data.client_initialized !== 'undefined' && (
                                <Text variant="small">Client initialized: {String(azureAgentValidation.data.client_initialized)}</Text>
                            )}
                            {azureAgentValidation.data.parameters && (
                                <>
                                    <Text variant="small">Endpoint set: {String(azureAgentValidation.data.parameters?.endpoint_set)}</Text>
                                    <Text variant="small">Bing connection set: {String(azureAgentValidation.data.parameters?.bing_connection_set)}</Text>
                                    {azureAgentValidation.data.parameters?.model && (
                                        <Text variant="small">Configured model: {String(azureAgentValidation.data.parameters?.model)}</Text>
                                    )}
                                </>
                            )}
                            {/* Live test details */}
                            {azureAgentValidation.data.mode && (
                                <Text variant="small">Live test mode: {azureAgentValidation.data.mode}</Text>
                            )}
                            {azureAgentValidation.data.details && (
                                <>
                                    {azureAgentValidation.data.details.thread_id && (
                                        <Text variant="small">Thread ID: {azureAgentValidation.data.details.thread_id}</Text>
                                    )}
                                    {azureAgentValidation.data.details.agent_id && (
                                        <Text variant="small">Agent ID: {azureAgentValidation.data.details.agent_id}</Text>
                                    )}
                                    {azureAgentValidation.data.details.model && (
                                        <Text variant="small">Model used: {String(azureAgentValidation.data.details.model)}</Text>
                                    )}
                                    {typeof azureAgentValidation.data.details.bing_configured !== 'undefined' && (
                                        <Text variant="small">Bing configured: {String(azureAgentValidation.data.details.bing_configured)}</Text>
                                    )}
                                    {typeof azureAgentValidation.data.details.bing_tool_attached !== 'undefined' && (
                                        <Text variant="small">Bing tool attached: {String(azureAgentValidation.data.details.bing_tool_attached)}</Text>
                                    )}
                                    {azureAgentValidation.data.details.bing_tool_error && (
                                        <Text variant="small" style={{ color: '#a4262c' }}>Bing tool error: {String(azureAgentValidation.data.details.bing_tool_error)}</Text>
                                    )}
                                    {typeof azureAgentValidation.data.details.citations_count !== 'undefined' && (
                                        <Text variant="small">Citations: {String(azureAgentValidation.data.details.citations_count)}</Text>
                                    )}
                                    {azureAgentValidation.data.details.run_status && (
                                        <Text variant="small">Run status: {String(azureAgentValidation.data.details.run_status)}</Text>
                                    )}
                                    {azureAgentValidation.data.details.last_error && (
                                        <Text variant="small" style={{ color: '#a4262c' }}>Run error: {String(azureAgentValidation.data.details.last_error)}</Text>
                                    )}
                                    {azureAgentValidation.data.details.no_citations_reason && (
                                        <Text variant="small" style={{ color: '#a4262c' }}>No citations reason: {String(azureAgentValidation.data.details.no_citations_reason)}</Text>
                                    )}
                                    {azureAgentValidation.data.details.tools_called && Array.isArray(azureAgentValidation.data.details.tools_called) && (
                                        <Text variant="small">Tools called: {azureAgentValidation.data.details.tools_called.join(', ')}</Text>
                                    )}
                                    {azureAgentValidation.data.details.assistant_preview && (
                                        <Text variant="small">Assistant preview: {azureAgentValidation.data.details.assistant_preview}</Text>
                                    )}
                                </>
                            )}
                            {!!(azureAgentValidation.data.errors || []).length && (
                                <div>
                                    <Text variant="smallPlus" style={{ fontWeight: 600, color: '#a4262c' }}>Errors</Text>
                                    <ul style={{ marginTop: 4 }}>
                                        {azureAgentValidation.data.errors.map((e: string, idx: number) => (<li key={idx}><Text variant="small" style={{ color: '#a4262c' }}>{e}</Text></li>))}
                                    </ul>
                                </div>
                            )}
                            {!!(azureAgentValidation.data.warnings || []).length && (
                                <div>
                                    <Text variant="smallPlus" style={{ fontWeight: 600, color: '#8a8886' }}>Warnings</Text>
                                    <ul style={{ marginTop: 4 }}>
                                        {azureAgentValidation.data.warnings.map((w: string, idx: number) => (<li key={idx}><Text variant="small">{w}</Text></li>))}
                                    </ul>
                                </div>
                            )}
                            {!!(azureAgentValidation.data.scopes || []).length && (
                                <div>
                                    <Text variant="smallPlus" style={{ fontWeight: 600 }}>Token Probes</Text>
                                    <ul style={{ marginTop: 4 }}>
                                        {azureAgentValidation.data.scopes.map((s: any, idx: number) => (
                                            <li key={idx}>
                                                <Text variant="small">{s.scope}: {s.success ? 'OK' : 'FAIL'}</Text>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </Stack>
                    )}
                </Stack>
            );
        };

        // Helper to render Azure OpenAI validation controls
        const renderAzureOpenAiValidation = () => {
            const runValidation = async () => {
                try {
                    setAzureOpenAiValidationLoading(true);
                    setAzureOpenAiValidation(null);
                    const resp = await authenticatedFetch('/api/admin/azure-openai/validate', { method: 'GET' });
                    const data = await resp.json();
                    setAzureOpenAiValidation({
                        success: data.success,
                        data: data.validation_result || data.data, // Handle both response formats
                        service_info: {
                            service: data.service,
                            timestamp: data.timestamp
                        },
                        error: data.error
                    });
                } catch (e) {
                    setAzureOpenAiValidation({ success: false, error: (e as any)?.message || 'Validation failed' });
                } finally {
                    setAzureOpenAiValidationLoading(false);
                }
            };

            const runLiveTest = async (mode: 'connectivity' | 'chat') => {
                try {
                    setAzureOpenAiValidationLoading(true);
                    const resp = await authenticatedFetch('/api/admin/azure-openai/test', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ mode, query: mode === 'chat' ? azureOpenAiTestQuery : undefined })
                    });
                    const data = await resp.json();
                    setAzureOpenAiValidation({
                        success: data.success,
                        data: data.test_result || data.data, // Handle both response formats
                        service_info: {
                            service: data.service,
                            test_mode: data.test_mode,
                            timestamp: data.timestamp
                        },
                        error: data.error
                    });
                } catch (e) {
                    setAzureOpenAiValidation({ success: false, error: (e as any)?.message || 'Live test failed' });
                } finally {
                    setAzureOpenAiValidationLoading(false);
                }
            };

            return (
                <Stack tokens={{ childrenGap: 10 }}>
                    <Separator />
                    <Text variant="medium" style={{ fontWeight: 600 }}>Azure OpenAI Validation</Text>
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton text={azureOpenAiValidationLoading ? 'Validating…' : 'Validate Azure OpenAI'} disabled={azureOpenAiValidationLoading} onClick={runValidation} />
                        <DefaultButton text={azureOpenAiValidationLoading ? 'Testing…' : 'Test Connectivity'} disabled={azureOpenAiValidationLoading} onClick={() => runLiveTest('connectivity')} />
                        <DefaultButton text={azureOpenAiValidationLoading ? 'Testing…' : 'Test Chat'} disabled={azureOpenAiValidationLoading} onClick={() => runLiveTest('chat')} />
                    </Stack>
                    {azureOpenAiValidation && (
                        <MessageBar messageBarType={azureOpenAiValidation.success ? MessageBarType.success : MessageBarType.error} isMultiline>
                            {azureOpenAiValidation.success ? 'Azure OpenAI configuration is working correctly.' : 'Azure OpenAI validation found issues.'}
                            {azureOpenAiValidation.error && <div>Error: {azureOpenAiValidation.error}</div>}
                            {azureOpenAiValidation.service_info && (
                                <div style={{ marginTop: 4, fontSize: '12px', opacity: 0.8 }}>
                                    Service: {azureOpenAiValidation.service_info.service} | Mode: {azureOpenAiValidation.service_info.test_mode || 'validation'} | {new Date(azureOpenAiValidation.service_info.timestamp).toLocaleTimeString()}
                                </div>
                            )}
                        </MessageBar>
                    )}
                    {/* Custom chat test query */}
                    <Stack tokens={{ childrenGap: 4 }} style={{ maxWidth: 700 }}>
                        <Label>Chat test query</Label>
                        <TextField
                            placeholder="Enter a question to test chat completion"
                            value={azureOpenAiTestQuery}
                            onChange={(_, v) => setAzureOpenAiTestQuery(v || '')}
                        />
                    </Stack>
                    {azureOpenAiValidation?.data && (
                        <Stack tokens={{ childrenGap: 6 }} style={{ background: '#faf9f8', padding: 12, borderRadius: 4 }}>
                            <Text variant="smallPlus" style={{ fontWeight: 600 }}>Diagnostics</Text>
                            {typeof azureOpenAiValidation.data.sdk_available !== 'undefined' && (
                                <Text variant="small">SDK available: {String(azureOpenAiValidation.data.sdk_available)}</Text>
                            )}
                            {typeof azureOpenAiValidation.data.client_initialized !== 'undefined' && (
                                <Text variant="small">Client initialized: {String(azureOpenAiValidation.data.client_initialized)}</Text>
                            )}
                            {typeof azureOpenAiValidation.data.connectivity_test !== 'undefined' && (
                                <Text variant="small">Connectivity test: {String(azureOpenAiValidation.data.connectivity_test)}</Text>
                            )}
                            {azureOpenAiValidation.data.parameters && (
                                <>
                                    <Text variant="small">Endpoint set: {String(azureOpenAiValidation.data.parameters?.endpoint_set)}</Text>
                                    <Text variant="small">
                                        Endpoint format valid: 
                                        <span style={{ 
                                            marginLeft: 4,
                                            color: azureOpenAiValidation.data.parameters?.endpoint_format_valid ? '#107c10' : '#d13438',
                                            fontWeight: 600
                                        }}>
                                            {String(azureOpenAiValidation.data.parameters?.endpoint_format_valid)}
                                        </span>
                                    </Text>
                                    <Text variant="small">Key set: {String(azureOpenAiValidation.data.parameters?.key_set)}</Text>
                                    <Text variant="small">Model set: {String(azureOpenAiValidation.data.parameters?.model_set)}</Text>
                                    <Text variant="small">Using admin config: {String(azureOpenAiValidation.data.parameters?.using_admin_config)}</Text>
                                </>
                            )}
                            {azureOpenAiValidation.data.details && (
                                <>
                                    {azureOpenAiValidation.data.details.auth_method && (
                                        <Text variant="small">
                                            <strong>Auth method: </strong>
                                            <span style={{ 
                                                color: azureOpenAiValidation.data.details.auth_method === 'api_key' ? '#667eea' : '#107c10',
                                                fontWeight: 600
                                            }}>
                                                {azureOpenAiValidation.data.details.auth_method === 'api_key' ? 'API Key' : 'Managed Identity'}
                                            </span>
                                            {azureOpenAiValidation.data.details.auth_note && (
                                                <span style={{ marginLeft: 8, color: '#666', fontSize: '11px' }}>
                                                    ({azureOpenAiValidation.data.details.auth_note})
                                                </span>
                                            )}
                                        </Text>
                                    )}
                                    {azureOpenAiValidation.data.details.endpoint_type && (
                                        <Text variant="small">
                                            <strong>Endpoint type: </strong>
                                            {azureOpenAiValidation.data.details.endpoint_type}
                                            {azureOpenAiValidation.data.details.endpoint_note && (
                                                <span style={{ marginLeft: 8, color: '#666', fontSize: '11px' }}>
                                                    ({azureOpenAiValidation.data.details.endpoint_note})
                                                </span>
                                            )}
                                        </Text>
                                    )}
                                    {azureOpenAiValidation.data.details.model && (
                                        <Text variant="small">Configured model: {String(azureOpenAiValidation.data.details.model)}</Text>
                                    )}
                                    {azureOpenAiValidation.data.details.api_version && (
                                        <Text variant="small">API version: {String(azureOpenAiValidation.data.details.api_version)}</Text>
                                    )}
                                    {azureOpenAiValidation.data.details.config_source && (
                                        <Text variant="small">Config source: {String(azureOpenAiValidation.data.details.config_source)}</Text>
                                    )}
                                    {typeof azureOpenAiValidation.data.details.available_models_count !== 'undefined' && (
                                        <Text variant="small">Available models: {String(azureOpenAiValidation.data.details.available_models_count)}</Text>
                                    )}
                                    {typeof azureOpenAiValidation.data.details.models_count !== 'undefined' && (
                                        <Text variant="small">Models found: {String(azureOpenAiValidation.data.details.models_count)}</Text>
                                    )}
                                    {typeof azureOpenAiValidation.data.details.tokens_used !== 'undefined' && (
                                        <Text variant="small">Tokens used: {String(azureOpenAiValidation.data.details.tokens_used)}</Text>
                                    )}
                                    {azureOpenAiValidation.data.details.model_used && (
                                        <Text variant="small">Model used: {String(azureOpenAiValidation.data.details.model_used)}</Text>
                                    )}
                                </>
                            )}
                            {azureOpenAiValidation.data.response && (
                                <>
                                    <Text variant="smallPlus" style={{ fontWeight: 600, marginTop: 8 }}>Test Response</Text>
                                    <Text variant="small" style={{ background: '#fff', padding: 8, borderRadius: 4, border: '1px solid #e1e1e1' }}>
                                        {String(azureOpenAiValidation.data.response)}
                                    </Text>
                                </>
                            )}
                            {!!(azureOpenAiValidation.data.errors || []).length && (
                                <div>
                                    <Text variant="smallPlus" style={{ fontWeight: 600, color: '#a4262c' }}>Errors</Text>
                                    <ul style={{ marginTop: 4 }}>
                                        {azureOpenAiValidation.data.errors.map((e: string, idx: number) => (<li key={idx}><Text variant="small" style={{ color: '#a4262c' }}>{e}</Text></li>))}
                                    </ul>
                                </div>
                            )}
                        </Stack>
                    )}
                </Stack>
            );
        };

        return (
            <Stack tokens={{ childrenGap: 24 }}>
                {/* Modern Gradient Section Header - matches AI Intent Analysis design */}
                <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 16,
                    padding: '28px 24px',
                    color: 'white',
                    boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)'
                }}>
                    <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 16 }}>
                            <div style={{
                                background: 'rgba(255, 255, 255, 0.15)',
                                borderRadius: 12,
                                padding: 12,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                {React.cloneElement(getIconForSection(configSection.category, configSection.title), {
                                    style: { fontSize: 24, color: 'white' }
                                })}
                            </div>
                            <Stack tokens={{ childrenGap: 4 }}>
                                <Text variant="xLarge" style={{ fontWeight: 700, color: 'white', margin: 0 }}>
                                    {configSection.title}
                                </Text>
                                {configSection.description && (
                                    <Text variant="medium" style={{ color: 'rgba(255, 255, 255, 0.85)', margin: 0 }}>
                                        {configSection.description}
                                    </Text>
                                )}
                            </Stack>
                        </Stack>
                        <Stack horizontal tokens={{ childrenGap: 12 }}>
                            <PrimaryButton
                                text={saving ? "Saving..." : "Save Changes"}
                                iconProps={{ iconName: 'Save' }}
                                onClick={() => saveSection(configSection.category)}
                                disabled={saving}
                                styles={{ 
                                    root: { 
                                        borderRadius: 8,
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                        color: 'white'
                                    },
                                    rootHovered: {
                                        background: 'rgba(255, 255, 255, 0.3)',
                                        border: '1px solid rgba(255, 255, 255, 0.4)'
                                    }
                                }}
                            />
                            <DefaultButton
                                text="Reset"
                                iconProps={{ iconName: 'Refresh' }}
                                styles={{ 
                                    root: { 
                                        borderRadius: 8,
                                        background: 'transparent',
                                        border: '1px solid rgba(255, 255, 255, 0.4)',
                                        color: 'white'
                                    },
                                    rootHovered: {
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        border: '1px solid rgba(255, 255, 255, 0.5)',
                                        color: 'white'
                                    }
                                }}
                                onClick={() => {
                                    const updatedConfig = { ...configuration };
                                    const section = updatedConfig.sections.find(s => s.category === configSection.category);
                                    if (section) {
                                        section.fields.forEach(field => {
                                            field.current_value = field.default_value;
                                        });
                                        setConfiguration(updatedConfig);
                                    }
                                }}
                            />
                        </Stack>
                    </Stack>
                </div>

                {/* Configuration Fields Card - Smart Auto-Grouped Layout */}
                <div style={{
                    background: 'white',
                    borderRadius: 16,
                    border: '1px solid #e5e7eb',
                    padding: 0,
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
                }}>
                    {/* Card Header */}
                    <div style={{
                        background: '#f8fafc',
                        borderBottom: '1px solid #e5e7eb',
                        padding: '16px 24px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12
                    }}>
                        <div style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #5a4f9e 100%)',
                            borderRadius: 8,
                            padding: 8,
                            display: 'flex'
                        }}>
                            <SettingsRegular style={{ fontSize: 18, color: 'white' }} />
                        </div>
                        <Text variant="large" style={{ fontWeight: 600, color: '#1a253c', margin: 0 }}>
                            Configuration Settings
                        </Text>
                    </div>
                    {/* Card Body */}
                    <div style={{ padding: 24 }}>
                        <ConfigSectionRenderer
                            fields={configSection.fields}
                            category={configSection.category}
                            onFieldChange={updateFieldValue}
                            onButtonClick={handleConfigButtonClick}
                            configuration={configuration}
                            sensitiveKeysStatus={sensitiveKeysStatus}
                        />
                    </div>
                </div>

                {/* Office 365 Cache Management Section */}
                {configSection.category === 'office365' && (
                    <div style={{
                        background: 'white',
                        borderRadius: 12,
                        border: '1px solid #e5e7eb',
                        padding: 24
                    }}>
                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }} style={{ marginBottom: 16 }}>
                            <DeleteRegular style={{ fontSize: 18, color: '#dc2626' }} />
                            <Text style={{ fontSize: 16, fontWeight: 600, color: '#374151' }}>Cache Management</Text>
                        </Stack>
                        <Text style={{ color: '#6b7280', fontSize: 14, marginBottom: 16, display: 'block' }}>
                            Clear Office 365 token cache when new permissions (like calendar access) have been added to the Azure AD app. 
                            Users will need to re-authenticate after clearing the cache.
                        </Text>
                        <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center">
                            <DefaultButton
                                iconProps={{ iconName: 'Delete' }}
                                disabled={clearingOffice365Cache}
                                onClick={handleClearOffice365Cache}
                                text={clearingOffice365Cache ? 'Clearing Cache...' : 'Clear Office 365 Cache'}
                                styles={{
                                    root: { 
                                        borderRadius: 6,
                                        backgroundColor: '#fee2e2',
                                        borderColor: '#fca5a5',
                                        color: '#dc2626'
                                    },
                                    rootHovered: {
                                        backgroundColor: '#fecaca',
                                        borderColor: '#f87171'
                                    }
                                }}
                            />
                            {office365CacheMessage && (
                                <div style={{ 
                                    padding: '8px 16px', 
                                    borderRadius: 6, 
                                    backgroundColor: office365CacheMessage.startsWith('✅') ? '#dcfce7' : '#fee2e2',
                                    color: office365CacheMessage.startsWith('✅') ? '#166534' : '#991b1b',
                                    fontSize: 13,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8
                                }}>
                                    {office365CacheMessage.startsWith('✅') ? (
                                        <CheckmarkCircleRegular style={{ fontSize: 16 }} />
                                    ) : (
                                        <WarningRegular style={{ fontSize: 16 }} />
                                    )}
                                    {office365CacheMessage.replace(/^(✅|❌)\s*/, '')}
                                </div>
                            )}
                        </Stack>
                    </div>
                )}

                {configSection.category === 'azure_agent' && renderAzureAgentValidation()}
                {configSection.category === 'azure_openai' && renderAzureOpenAiValidation()}
            </Stack>
        );
    };

    const renderCloudManagement = () => {
        if (loadingCloudInfo) {
            return (
                <Stack horizontalAlign="center" tokens={{ childrenGap: 20 }}>
                    <Spinner label="Loading cloud management information..." size={3} />
                </Stack>
            );
        }

        if (!cloudManagementInfo) {
            return (
                <Stack tokens={{ childrenGap: 20 }}>
                    <Text variant="large" style={{ fontWeight: 600, color: '#1a253c' }}>
                        Cloud Management System
                    </Text>
                    <MessageBar messageBarType={MessageBarType.warning}>
                        Failed to load cloud management information. Please try refreshing.
                    </MessageBar>
                    <PrimaryButton
                        text="Reload Information"
                        onClick={loadCloudManagementInfo}
                    />
                </Stack>
            );
        }

        const {
            current_user,
            access_control,
            cosmos_db,
            azure_subscriptions,
            rag_system,
            system_status,
            endpoints
        } = cloudManagementInfo;
    const googleConfig = (rag_system as any)?.google_config || {};

        return (
            <Stack tokens={{ childrenGap: 30 }}>
                <Text variant="large" style={{ fontWeight: 600, color: '#1a253c' }}>
                    Cloud Management System
                </Text>

                {/* System Status */}
                <Stack tokens={{ childrenGap: 15 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>System Status</Text>
                    <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Cosmos DB Status</Label>
                            <MessageBar 
                                messageBarType={system_status.cosmos_healthy ? MessageBarType.success : MessageBarType.error}
                                isMultiline={false}
                            >
                                {system_status.cosmos_healthy ? 'Connected' : 'Disconnected'}
                            </MessageBar>
                        </div>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Recent Data</Label>
                            <MessageBar 
                                messageBarType={system_status.recent_data_available ? MessageBarType.success : MessageBarType.warning}
                                isMultiline={false}
                            >
                                {system_status.recent_data_available ? 'Available' : 'No recent data'}
                            </MessageBar>
                        </div>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Subscriptions Monitored</Label>
                            <Text variant="medium">{system_status.subscriptions_monitored}</Text>
                        </div>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Total Recommendations</Label>
                            <Text variant="medium">{system_status.total_recommendations}</Text>
                        </div>
                    </Stack>
                    <div>
                        <Label>Last Data Collection</Label>
                        <Text variant="medium">{system_status.last_data_collection}</Text>
                    </div>
                </Stack>

                <Separator />

                {/* Current User & Access Control */}
                <Stack tokens={{ childrenGap: 15 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>Access Control</Text>
                    <Stack horizontal tokens={{ childrenGap: 30 }} wrap>
                        <div style={{ minWidth: '300px' }}>
                            <Label>Current User</Label>
                            <Stack tokens={{ childrenGap: 5 }}>
                                <Text variant="medium">{current_user.name} ({current_user.email})</Text>
                                <Text variant="small">
                                    {current_user.is_super_admin ? 'Super Administrator' : 'Administrator'}
                                </Text>
                            </Stack>
                        </div>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Admin Users</Label>
                            <Text variant="medium">{access_control.total_admin_users} total</Text>
                        </div>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Super Admin Users</Label>
                            <Text variant="medium">{access_control.total_super_admin_users} total</Text>
                        </div>
                    </Stack>
                    
                    <div>
                        <Label>Admin Email Addresses</Label>
                        {editingCloudConfig ? (
                            <EntraUserPicker
                                selectedEmails={
                                    cloudConfigChanges['access_control.admin_emails'] !== undefined
                                        ? cloudConfigChanges['access_control.admin_emails']
                                        : access_control.admin_emails || []
                                }
                                onSelectionChange={(emails) => updateCloudConfigValue('access_control.admin_emails', emails)}
                                label=""
                                placeholder="Search users in Azure Entra..."
                            />
                        ) : (
                            <TextField 
                                multiline 
                                rows={3} 
                                readOnly={true}
                                value={access_control.admin_emails.join('\n')}
                                styles={{ field: { backgroundColor: '#f3f2f1' } }}
                            />
                        )}
                    </div>
                    
                    <div>
                        <Label>Admin Azure AD Groups</Label>
                        {editingCloudConfig ? (
                            <EntraGroupPicker
                                selectedGroupIds={
                                    cloudConfigChanges['access_control.admin_groups'] !== undefined
                                        ? cloudConfigChanges['access_control.admin_groups']
                                        : access_control.admin_groups || []
                                }
                                onSelectionChange={(groups) => updateCloudConfigValue('access_control.admin_groups', groups)}
                                label=""
                                placeholder="Search groups in Azure Entra..."
                            />
                        ) : (
                            <TextField 
                                multiline 
                                rows={2} 
                                readOnly={true}
                                value={(access_control.admin_groups || []).join('\n')}
                                styles={{ field: { backgroundColor: '#f3f2f1' } }}
                            />
                        )}
                    </div>
                </Stack>

                <Separator />

                {/* Azure Subscriptions */}
                <Stack tokens={{ childrenGap: 15 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>Azure Subscriptions</Text>
                    <div>
                        <Label>Primary Subscription ID</Label>
                        <TextField 
                            readOnly={!editingCloudConfig}
                            value={getCloudConfigValue(azure_subscriptions.primary_subscription_id, 'azure_subscriptions.primary_subscription_id')}
                            onChange={(_, newValue) => updateCloudConfigValue('azure_subscriptions.primary_subscription_id', newValue)}
                            styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                        />
                    </div>
                    
                    <div>
                        <Label>Additional Subscription IDs (one per line)</Label>
                        <TextField 
                            multiline 
                            rows={3} 
                            readOnly={!editingCloudConfig}
                            value={getCloudConfigValue(
                                azure_subscriptions.additional_subscriptions?.join('\n') || '', 
                                'azure_subscriptions.additional_subscriptions'
                            )}
                            onChange={(_, newValue) => updateCloudConfigValue('azure_subscriptions.additional_subscriptions', newValue)}
                            styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                            placeholder="Enter additional subscription IDs, one per line"
                        />
                    </div>
                    
                    <div>
                        <Label>Azure Tenant ID</Label>
                        <TextField 
                            readOnly={!editingCloudConfig}
                            value={getCloudConfigValue(azure_subscriptions.tenant_id, 'azure_subscriptions.tenant_id')}
                            onChange={(_, newValue) => updateCloudConfigValue('azure_subscriptions.tenant_id', newValue)}
                            styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                        />
                    </div>
                </Stack>

                <Separator />

                {/* Cosmos DB Configuration */}
                <Stack tokens={{ childrenGap: 15 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>Cosmos DB Configuration</Text>
                    <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                        <div style={{ minWidth: '250px' }}>
                            <Label>Account Name</Label>
                            <TextField 
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(cosmos_db.account_name, 'cosmos_db.account_name')}
                                onChange={(_, newValue) => updateCloudConfigValue('cosmos_db.account_name', newValue)}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                            />
                        </div>
                        <div style={{ minWidth: '250px' }}>
                            <Label>Database Name</Label>
                            <TextField 
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(cosmos_db.database_name, 'cosmos_db.database_name')}
                                onChange={(_, newValue) => updateCloudConfigValue('cosmos_db.database_name', newValue)}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                            />
                        </div>
                    </Stack>
                    <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                        <div style={{ minWidth: '250px' }}>
                            <Label>Main Container</Label>
                            <TextField 
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(cosmos_db.main_container, 'cosmos_db.main_container')}
                                onChange={(_, newValue) => updateCloudConfigValue('cosmos_db.main_container', newValue)}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                            />
                        </div>
                        <div style={{ minWidth: '250px' }}>
                            <Label>Embeddings Container</Label>
                            <TextField 
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(cosmos_db.embeddings_container, 'cosmos_db.embeddings_container')}
                                onChange={(_, newValue) => updateCloudConfigValue('cosmos_db.embeddings_container', newValue)}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                            />
                        </div>
                    </Stack>
                    <div style={{ maxWidth: '250px' }}>
                        <Label>Summaries Container</Label>
                        <TextField 
                            readOnly={!editingCloudConfig}
                            value={getCloudConfigValue(cosmos_db.summaries_container, 'cosmos_db.summaries_container')}
                            onChange={(_, newValue) => updateCloudConfigValue('cosmos_db.summaries_container', newValue)}
                            styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                        />
                    </div>
                </Stack>

                <Separator />

                {/* RAG System Configuration */}
                <Stack tokens={{ childrenGap: 15 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>RAG System Configuration</Text>
                    
                    <div>
                        <Label>RAG Prompt Template</Label>
                        <TextField 
                            multiline 
                            rows={12} 
                            readOnly={!editingCloudConfig}
                            value={getCloudConfigValue(rag_system.prompt_template, 'rag_system.prompt_template')}
                            onChange={(_, newValue) => updateCloudConfigValue('rag_system.prompt_template', newValue)}
                            styles={{ 
                                field: { 
                                    backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1', 
                                    fontFamily: 'Monaco, Consolas, monospace', 
                                    fontSize: '12px' 
                                } 
                            }}
                            placeholder="Enter the RAG system prompt template..."
                        />
                    </div>
                    
                    <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Temperature</Label>
                            <TextField 
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(rag_system.temperature?.toString() || '0.3', 'rag_system.temperature')}
                                onChange={(_, newValue) => updateCloudConfigValue('rag_system.temperature', parseFloat(newValue || '0.3'))}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                                type="number"
                                step="0.1"
                                min="0"
                                max="2"
                            />
                        </div>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Max Tokens</Label>
                            <TextField 
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(rag_system.max_tokens?.toString() || '1500', 'rag_system.max_tokens')}
                                onChange={(_, newValue) => updateCloudConfigValue('rag_system.max_tokens', parseInt(newValue || '1500'))}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                                type="number"
                                min="1"
                                max="4000"
                            />
                        </div>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Max Context Length</Label>
                            <TextField 
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(rag_system.max_context_length?.toString() || '5', 'rag_system.max_context_length')}
                                onChange={(_, newValue) => updateCloudConfigValue('rag_system.max_context_length', parseInt(newValue || '5'))}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                                type="number"
                                min="1"
                                max="20"
                            />
                        </div>
                        <div style={{ minWidth: '200px' }}>
                            <Label>Similarity Threshold</Label>
                            <TextField 
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(rag_system.similarity_threshold?.toString() || '0.7', 'rag_system.similarity_threshold')}
                                onChange={(_, newValue) => updateCloudConfigValue('rag_system.similarity_threshold', parseFloat(newValue || '0.7'))}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                                type="number"
                                step="0.1"
                                min="0"
                                max="1"
                            />
                        </div>
                    </Stack>
                    
                    <MessageBar messageBarType={MessageBarType.info}>
                        <Text>
                            <strong>RAG Parameters:</strong> Temperature controls randomness (0.0-2.0), Max Tokens limits response length, 
                            Max Context Length sets number of retrieved documents, and Similarity Threshold filters relevant content (0.0-1.0).
                        </Text>
                    </MessageBar>
                </Stack>

                <Separator />

                {/* Google Cloud (GCP) Configuration - Cloud Management only */}
                <Stack tokens={{ childrenGap: 15 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>Google Cloud (GCP) Configuration</Text>

                    <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
                        <div style={{ minWidth: '250px' }}>
                            <Label>GCP Projects</Label>
                            <TextField
                                readOnly={!editingCloudConfig}
                                value={getCloudConfigValue(googleConfig.gcp_projects || '', 'cloud_gcp.projects')}
                                onChange={(_, newValue) => updateCloudConfigValue('cloud_gcp.projects', newValue)}
                                styles={{ field: { backgroundColor: editingCloudConfig ? '#ffffff' : '#f3f2f1' } }}
                                placeholder="project-a,project-b or one per line"
                                multiline
                                rows={3}
                            />
                        </div>
                    </Stack>

                    <div>
                        <Label>Cloud Management GCP Credentials</Label>
                        {!editingCloudConfig ? (
                            <MessageBar messageBarType={googleConfig.cloud_gcp_credentials_configured ? MessageBarType.success : MessageBarType.warning} isMultiline={false}>
                                {googleConfig.cloud_gcp_credentials_configured ? 'Configured' : 'Not Configured'}
                            </MessageBar>
                        ) : (
                            <TextField
                                multiline
                                rows={8}
                                placeholder='Paste Cloud Management service account JSON here or provide a path (e.g., /path/to/key.json)'
                                onChange={(_, newValue) => updateCloudConfigValue('cloud_gcp.credentials', newValue)}
                                styles={{ field: { backgroundColor: '#ffffff', fontFamily: 'Monaco, Consolas, monospace', fontSize: 12 } }}
                            />
                        )}
                    </div>

                    <MessageBar messageBarType={MessageBarType.info}>
                        <Text>
                            These credentials are used only by the Cloud Management system. Inline JSON is supported and recommended.
                        </Text>
                    </MessageBar>
                </Stack>

                <Separator />

                {/* API Endpoints */}
                <Stack tokens={{ childrenGap: 15 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>API Endpoints</Text>
                    <Stack tokens={{ childrenGap: 10 }}>
                        {Object.entries(endpoints).map(([name, endpoint]) => (
                            <Stack horizontal key={name} tokens={{ childrenGap: 10 }} verticalAlign="center">
                                <div style={{ minWidth: '150px' }}>
                                    <Text variant="medium" style={{ fontWeight: 600 }}>{name}:</Text>
                                </div>
                                <TextField 
                                    readOnly 
                                    value={endpoint as string} 
                                    styles={{ 
                                        field: { backgroundColor: '#f3f2f1', fontFamily: 'Monaco, Consolas, monospace' },
                                        root: { flex: 1 }
                                    }} 
                                />
                            </Stack>
                        ))}
                    </Stack>
                </Stack>

                <Separator />

                {/* Actions */}
                <Stack tokens={{ childrenGap: 15 }}>
                    <Text variant="medium" style={{ fontWeight: 600 }}>Actions</Text>
                    <Stack horizontal tokens={{ childrenGap: 15 }} wrap>
                        <PrimaryButton
                            text="Refresh Information"
                            iconProps={{ iconName: 'Refresh' }}
                            onClick={loadCloudManagementInfo}
                            disabled={loadingCloudInfo || editingCloudConfig}
                        />
                        {!editingCloudConfig ? (
                            <DefaultButton
                                text="Edit Configuration"
                                iconProps={{ iconName: 'Edit' }}
                                onClick={() => setEditingCloudConfig(true)}
                                styles={{
                                    root: { 
                                        backgroundColor: '#667eea',
                                        borderColor: '#667eea',
                                        color: 'white'
                                    },
                                    rootHovered: {
                                        backgroundColor: '#5a4f9e',
                                        borderColor: '#5a4f9e'
                                    }
                                }}
                            />
                        ) : (
                            <>
                                <PrimaryButton
                                    text={saving ? "Saving..." : "Save Changes"}
                                    iconProps={{ iconName: 'Save' }}
                                    onClick={saveCloudManagementConfig}
                                    disabled={saving}
                                />
                                <DefaultButton
                                    text="Cancel"
                                    iconProps={{ iconName: 'Cancel' }}
                                    onClick={resetCloudConfigChanges}
                                    disabled={saving}
                                />
                            </>
                        )}
                    </Stack>
                    {editingCloudConfig && (
                        <MessageBar messageBarType={MessageBarType.warning}>
                            <Text>
                                <strong>Edit Mode:</strong> You are currently editing the Cloud Management configuration. 
                                Make your changes and click "Save Changes" to apply them, or "Cancel" to discard changes.
                            </Text>
                        </MessageBar>
                    )}
                </Stack>
            </Stack>
        );
    };

    // AI Connector Hub UI removed from admin config page

    const renderSharePointConnectorHub = () => {
        return (
            <Stack tokens={{ childrenGap: 20 }}>
                <Stack tokens={{ childrenGap: 8 }}>
                    <Text variant="xLarge" style={{ fontWeight: 600 }}>
                        SharePoint Connector Hub
                    </Text>
                    <Text variant="medium" style={{ color: '#666' }}>
                        Configure global SharePoint credentials used by all SharePoint connector jobs
                    </Text>
                </Stack>

                {message && (
                    <MessageBar 
                        messageBarType={message.type}
                        onDismiss={() => setMessage(null)}
                    >
                        {message.text}
                    </MessageBar>
                )}

                <Stack tokens={{ childrenGap: 20 }}>
                    {/* SharePoint Global Credentials */}
                    <Stack tokens={{ childrenGap: 15 }}>
                        <Text variant="large" style={{ fontWeight: 600 }}>Global SharePoint Credentials</Text>
                        <Text variant="medium" style={{ color: '#666' }}>
                            Configure Azure App Registration credentials for SharePoint connections. These credentials will be used for all SharePoint connector jobs.
                        </Text>
                        
                        <Stack tokens={{ childrenGap: 12 }}>
                            <TextField
                                label="Azure Client ID"
                                placeholder="Enter Azure AD Application (Client) ID"
                                description="The Application (Client) ID from Azure AD App Registration"
                                value={sharePointCredentials.client_id}
                                onChange={(_, newValue) => setSharePointCredentials(prev => ({ ...prev, client_id: newValue || '' }))}
                                disabled={sharepointCredentialsLoading}
                                canRevealPassword
                                revealPasswordAriaLabel="Show Client ID"
                            />
                            
                            <TextField
                                label="Azure Tenant ID"
                                placeholder="Enter Azure AD Tenant (Directory) ID"
                                description="The Tenant (Directory) ID from Azure AD"
                                value={sharePointCredentials.tenant_id}
                                onChange={(_, newValue) => setSharePointCredentials(prev => ({ ...prev, tenant_id: newValue || '' }))}
                                disabled={sharepointCredentialsLoading}
                                canRevealPassword
                                revealPasswordAriaLabel="Show Tenant ID"
                            />
                            
                            <TextField
                                label="Azure Client Secret"
                                type="password"
                                placeholder="Enter Azure AD Application Client Secret"
                                description="The client secret from Azure AD App Registration"
                                value={sharePointCredentials.client_secret}
                                onChange={(_, newValue) => setSharePointCredentials(prev => ({ ...prev, client_secret: newValue || '' }))}
                                disabled={sharepointCredentialsLoading}
                                canRevealPassword
                                revealPasswordAriaLabel="Show Client Secret"
                            />
                        </Stack>

                        <Stack horizontal tokens={{ childrenGap: 12 }}>
                            <PrimaryButton
                                text="Save Credentials"
                                iconProps={{ iconName: 'Save' }}
                                onClick={saveSharePointCredentials}
                                disabled={sharepointCredentialsLoading || testingCredentials}
                            />
                            <DefaultButton
                                text="Test Connection"
                                iconProps={{ iconName: 'PlugConnected' }}
                                onClick={testSharePointCredentials}
                                disabled={sharepointCredentialsLoading || testingCredentials || !sharePointCredentials.client_id || !sharePointCredentials.tenant_id || !sharePointCredentials.client_secret}
                            />
                            {(sharepointCredentialsLoading || testingCredentials) && (
                                <Spinner size={SpinnerSize.small} />
                            )}
                        </Stack>

                        <MessageBar messageBarType={MessageBarType.info}>
                            <Text variant="small">
                                <strong>Required Permissions:</strong> These credentials need the following Microsoft Graph API permissions:
                                <br />• Sites.Read.All (to read SharePoint sites)
                                <br />• Files.Read.All (to read documents)
                                <br />• User.Read.All (for ACL extraction)
                            </Text>
                        </MessageBar>
                    </Stack>

                    <Separator />

                    {/* Configuration Status */}
                    <Stack tokens={{ childrenGap: 15 }}>
                        <Text variant="large" style={{ fontWeight: 600 }}>Configuration Status</Text>
                        
                        <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                            gap: '16px' 
                        }}>
                            <div style={{ 
                                padding: '16px', 
                                border: '1px solid #e1e1e1', 
                                borderRadius: '8px',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <PlugConnectedRegular style={{ fontSize: 24, color: sharePointCredentials.client_id ? '#107c10' : '#d83b01' }} />
                                    <Text variant="medium" style={{ fontWeight: 600 }}>Client ID</Text>
                                    <Text variant="large" style={{ color: sharePointCredentials.client_id ? '#107c10' : '#d83b01' }}>
                                        {sharePointCredentials.client_id ? 'Configured' : 'Not Set'}
                                    </Text>
                                    <Text variant="small" style={{ color: '#666' }}>
                                        Azure AD Application ID
                                    </Text>
                                </Stack>
                            </div>

                            <div style={{ 
                                padding: '16px', 
                                border: '1px solid #e1e1e1', 
                                borderRadius: '8px',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <PersonRegular style={{ fontSize: 24, color: sharePointCredentials.tenant_id ? '#107c10' : '#d83b01' }} />
                                    <Text variant="medium" style={{ fontWeight: 600 }}>Tenant ID</Text>
                                    <Text variant="large" style={{ color: sharePointCredentials.tenant_id ? '#107c10' : '#d83b01' }}>
                                        {sharePointCredentials.tenant_id ? 'Configured' : 'Not Set'}
                                    </Text>
                                    <Text variant="small" style={{ color: '#666' }}>
                                        Azure AD Directory ID
                                    </Text>
                                </Stack>
                            </div>

                            <div style={{ 
                                padding: '16px', 
                                border: '1px solid #e1e1e1', 
                                borderRadius: '8px',
                                backgroundColor: '#f8f9fa'
                            }}>
                                <Stack tokens={{ childrenGap: 8 }}>
                                    <LockClosedRegular style={{ fontSize: 24, color: sharePointCredentials.client_secret ? '#107c10' : '#d83b01' }} />
                                    <Text variant="medium" style={{ fontWeight: 600 }}>Client Secret</Text>
                                    <Text variant="large" style={{ color: sharePointCredentials.client_secret ? '#107c10' : '#d83b01' }}>
                                        {sharePointCredentials.client_secret ? 'Configured' : 'Not Set'}
                                    </Text>
                                    <Text variant="small" style={{ color: '#666' }}>
                                        Azure AD Application Secret
                                    </Text>
                                </Stack>
                            </div>
                        </div>
                    </Stack>

                    <Separator />

                    {/* Usage Information */}
                    <Stack tokens={{ childrenGap: 15 }}>
                        <Text variant="large" style={{ fontWeight: 600 }}>Usage Information</Text>
                        
                        <MessageBar messageBarType={MessageBarType.info}>
                            <Text variant="small">
                                Once configured, these credentials will be automatically used by all SharePoint connector jobs. 
                                Individual jobs will no longer prompt for credentials - they will use these global settings.
                            </Text>
                        </MessageBar>

                        <Stack tokens={{ childrenGap: 12 }}>
                            <Text variant="medium" style={{ fontWeight: 600 }}>How to obtain these credentials:</Text>
                            <Text variant="small">
                                1. Go to the Azure Portal → Azure Active Directory → App registrations
                                <br />2. Create a new app registration or use an existing one
                                <br />3. Note the Application (client) ID and Directory (tenant) ID
                                <br />4. Go to Certificates & secrets → New client secret
                                <br />5. Copy the client secret value (available only once)
                                <br />6. Go to API permissions → Add the required Microsoft Graph permissions listed above
                                <br />7. Grant admin consent for the permissions
                            </Text>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>
        );
    };

    const getSelectedSectionInfo = () => {
        const adminSections = getAdminSections();
        return adminSections.find(section => section.id === selectedSection);
    };

    const updateCloudConfigValue = (path: string, value: any) => {
        // Handle special array fields
        let processedValue = value;
        if (path === 'access_control.admin_emails' || path === 'access_control.admin_groups') {
            // EntraUserPicker/EntraGroupPicker passes arrays directly
            // Also handle legacy newline-separated string format for backward compatibility
            if (typeof value === 'string') {
                processedValue = value.split('\n').map(line => line.trim()).filter(line => line.length > 0);
            } else if (Array.isArray(value)) {
                // Already an array from EntraUserPicker/EntraGroupPicker
                processedValue = value.filter(item => item && item.length > 0);
            }
        } else if (path === 'azure_subscriptions.additional_subscriptions') {
            // Handle additional subscriptions array
            processedValue = typeof value === 'string' 
                ? value.split('\n').map(line => line.trim()).filter(line => line.length > 0)
                : value;
        }
        
        setCloudConfigChanges(prev => ({
            ...prev,
            [path]: processedValue
        }));
    };

    const getCloudConfigValue = (originalValue: any, path: string) => {
        const changedValue = cloudConfigChanges[path];
        
        // If we have a changed value, use it
        if (changedValue !== undefined) {
            // For array fields that should be displayed as newline-separated strings
            if (path === 'access_control.admin_emails' || path === 'access_control.admin_groups' || path === 'azure_subscriptions.additional_subscriptions') {
                return Array.isArray(changedValue) ? changedValue.join('\n') : changedValue;
            }
            return changedValue;
        }
        
        // Otherwise use original value
        // For array fields that should be displayed as newline-separated strings
        if (path === 'access_control.admin_emails' || path === 'access_control.admin_groups' || path === 'azure_subscriptions.additional_subscriptions') {
            return Array.isArray(originalValue) ? originalValue.join('\n') : (originalValue || '');
        }
        
        return originalValue;
    };

    const saveCloudManagementConfig = async () => {
        try {
            setSaving(true);
            const response = await authenticatedFetch('/api/admin/cloud-management/config', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    config_updates: cloudConfigChanges,
                    updated_by: 'admin_ui'
                }),
            });

            const result = await response.json();
            
            if (result.success) {
                setMessage({ 
                    text: 'Cloud Management configuration saved successfully', 
                    type: MessageBarType.success 
                });
                setCloudConfigChanges({});
                setEditingCloudConfig(false);
                await loadCloudManagementInfo(); // Reload to show updated values
            } else {
                setMessage({ 
                    text: result.error || 'Failed to save cloud management configuration', 
                    type: MessageBarType.error 
                });
            }
        } catch (error) {
            console.error('Error saving cloud management config:', error);
            setMessage({ 
                text: 'Error saving cloud management configuration', 
                type: MessageBarType.error 
            });
        } finally {
            setSaving(false);
        }
    };

    const resetCloudConfigChanges = () => {
        setCloudConfigChanges({});
        setEditingCloudConfig(false);
    };

    if (loading) {
        return (
            <div className={styles.adminPage}>
                <div className={styles.loadingContainer}>
                    <Stack horizontalAlign="center" tokens={{ childrenGap: 20 }}>
                        <Spinner label="Loading configuration..." size={3} />
                    </Stack>
                </div>
            </div>
        );
    }

    if (!configuration) {
        return (
            <div className={styles.adminPage}>
                <div className={styles.loadingContainer}>
                    <MessageBar messageBarType={MessageBarType.error}>
                        Failed to load configuration. Please try refreshing the page.
                    </MessageBar>
                </div>
            </div>
        );
    }

    const selectedSectionInfo = getSelectedSectionInfo();
    const adminSections = getAdminSections();

    return (
        <div className={styles.adminPage}>
            <Stack horizontal tokens={{ childrenGap: 0 }} styles={{ root: { height: '100%' } }}>
                {/* Left Navigation */}
                <div className={styles.leftNav}>
                    <Stack horizontal tokens={{ childrenGap: 10 }} style={{ padding: '20px 20px 10px', alignItems: 'center' }}>
                        <SettingsRegular style={{ fontSize: 28, color: '#667eea' }} />
                        <Text variant="large" style={{ fontWeight: 600, color: '#1a253c' }}>
                            Admin Configuration
                        </Text>
                    </Stack>
                    
                    <Separator />
                    
                    {adminSections.map(section => (
                        <div
                            key={section.id}
                            className={`${styles.navItem} ${selectedSection === section.id ? styles.selected : ''}`}
                            onClick={() => setSelectedSection(section.id)}
                        >
                            <Stack horizontal tokens={{ childrenGap: 12 }} verticalAlign="center" style={{ overflow: 'hidden' }}>
                                <div style={{ flexShrink: 0 }}>{section.icon}</div>
                                <Stack tokens={{ childrenGap: 2 }} style={{ overflow: 'hidden', minWidth: 0 }}>
                                    <Text variant="medium" style={{ 
                                        fontWeight: selectedSection === section.id ? 600 : 500,
                                        color: selectedSection === section.id ? '#667eea' : '#374151',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {section.title}
                                    </Text>
                                    <Text variant="small" style={{ 
                                        color: '#6b7280', 
                                        lineHeight: '16px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {section.description}
                                    </Text>
                                </Stack>
                            </Stack>
                        </div>
                    ))}
                </div>

                {/* Main Content */}
                <Stack className={styles.mainContent} tokens={{ childrenGap: 20 }}>
                    {selectedSectionInfo && (
                        <>
                            <Stack horizontal tokens={{ childrenGap: 15 }} verticalAlign="center" className={styles.sectionHeader}>
                                {selectedSectionInfo.icon}
                                <Stack tokens={{ childrenGap: 5 }}>
                                    <Text variant="xxLarge" style={{ fontWeight: 600, color: '#1a253c' }}>
                                        {selectedSectionInfo.title}
                                    </Text>
                                    <Text variant="medium" style={{ color: '#666' }}>
                                        {selectedSectionInfo.description}
                                    </Text>
                                </Stack>
                            </Stack>
                            <Separator />
                        </>
                    )}

                    {message && (
                        <MessageBar
                            messageBarType={message.type}
                            onDismiss={() => setMessage(null)}
                        >
                            {message.text}
                        </MessageBar>
                    )}

                    <div className={styles.sectionContent}>
                        {selectedSection === 'overview' ? (
                            renderOverview()
                        ) : selectedSection === 'content-editors' ? (
                            <Stack tokens={{ childrenGap: 24 }}>
                                <Text variant="large" style={{ color: '#1a253c' }}>
                                    Manage help and guide content for users and administrators.
                                </Text>
                                <Stack horizontal tokens={{ childrenGap: 16 }} wrap>
                                    <div style={{
                                        background: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 12,
                                        padding: 24,
                                        flex: '1 1 280px',
                                        maxWidth: 400
                                    }}>
                                        <Stack tokens={{ childrenGap: 12 }}>
                                            <Text variant="mediumPlus" style={{ fontWeight: 600 }}>User Help</Text>
                                            <Text variant="small" style={{ color: '#6b7280' }}>
                                                Edit the help content shown when users click the Help button.
                                            </Text>
                                            <PrimaryButton
                                                text="Open User Help Editor"
                                                iconProps={{ iconName: 'Edit' }}
                                                onClick={() => setIsHelpEditorOpen(true)}
                                                styles={{ root: { borderRadius: 6 } }}
                                            />
                                        </Stack>
                                    </div>
                                    <div style={{
                                        background: 'white',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: 12,
                                        padding: 24,
                                        flex: '1 1 280px',
                                        maxWidth: 400
                                    }}>
                                        <Stack tokens={{ childrenGap: 12 }}>
                                            <Text variant="mediumPlus" style={{ fontWeight: 600 }}>Admin Guide</Text>
                                            <Text variant="small" style={{ color: '#6b7280' }}>
                                                Edit the configuration guide shown to administrators.
                                            </Text>
                                            <DefaultButton
                                                text="Open Admin Guide Editor"
                                                iconProps={{ iconName: 'Edit' }}
                                                onClick={() => setIsAdminGuideEditorOpen(true)}
                                                styles={{ root: { borderRadius: 6 } }}
                                            />
                                        </Stack>
                                    </div>
                                </Stack>
                            </Stack>
                        ) : selectedSection === 'ui-management' ? (
                            <UIManagement />
                        ) : selectedSection === 'news-management' ? (
                            <NewsManagement />
                        ) : selectedSection === 'agent-management' ? (
                            <AgentManagement />
                        ) : selectedSection === 'structured-data-sources' ? (
                            <StructuredDataSources />
                        ) : selectedSection === 'feature-permissions' ? (
                            <FeaturePermissions />
                        ) : selectedSection === 'token-limits' ? (
                            <TokenLimitsManagement />
                        ) : selectedSection === 'presentation-templates' ? (
                            <AdminPresentationTemplates />
                        ) : selectedSection === 'cloud-management' ? (
                            renderCloudManagement()
                        ) : (
                            renderConfigSection(selectedSectionInfo!)
                        )}
                    </div>
                </Stack>
            </Stack>
            
            {/* Help Content Editor */}
            <HelpEditor
                isVisible={isHelpEditorOpen}
                onDismiss={() => setIsHelpEditorOpen(false)}
            />

            {/* Admin Guide Editor */}
            <AdminGuideEditor
                isVisible={isAdminGuideEditorOpen}
                onDismiss={() => setIsAdminGuideEditorOpen(false)}
            />

            {/* Grounding Instruction Editor Panel */}
            <Panel
                isOpen={showGroundingPanel}
                type={PanelType.medium}
                onDismiss={() => setShowGroundingPanel(false)}
                headerText={`Edit Grounding Instructions: ${editingGroundingLabel}`}
                isFooterAtBottom={true}
                onRenderFooterContent={() => (
                    <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <PrimaryButton
                            iconProps={{ iconName: 'Save' }}
                            text="Save"
                            onClick={saveGrounding}
                        />
                        <DefaultButton 
                            text="Cancel"
                            onClick={() => setShowGroundingPanel(false)}
                        />
                    </Stack>
                )}
            >
                <Stack tokens={{ childrenGap: 16 }}>
                    <Text variant="medium">
                        These instructions guide how the AI responds when using this specific data source. 
                        Be specific about:
                    </Text>
                    <ul style={{ margin: '0 0 16px 16px', color: '#666' }}>
                        <li>How strictly to follow the source material</li>
                        <li>What to do when information is incomplete</li>
                        <li>How to cite sources properly</li>
                        <li>What types of responses to avoid</li>
                    </ul>
                    
                    <TextField
                        multiline
                        rows={20}
                        value={groundingText}
                        onChange={(_, value) => setGroundingText(value || '')}
                        placeholder="Enter grounding instructions..."
                        description="Use clear, specific instructions that will help the AI provide accurate and grounded responses."
                        styles={{
                            field: {
                                fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                                fontSize: '12px'
                            }
                        }}
                    />
                    
                    <MessageBar messageBarType={MessageBarType.info}>
                        <Text variant="small">
                            <strong>Tip:</strong> Instructions are processed in Hebrew and English. 
                            Use numbered lists and clear directives for best results.
                        </Text>
                    </MessageBar>
                </Stack>
            </Panel>
        </div>
    );
};

export default AdminConfigPage;

// --- Prompt Preview Panel Component ---

const PromptPreviewPanel: React.FC = () => {
    const [question, setQuestion] = React.useState('What is our remote work policy?');
    const [groundingIndex, setGroundingIndex] = React.useState('');
    const [hasDocContext, setHasDocContext] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState<any>(null);
    const [error, setError] = React.useState<string | null>(null);

    const runPreview = async () => {
        setLoading(true); setError(null);
        try {
            const resp = await authenticatedFetch('/api/admin/prompt-preview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    question,
                    grounding_index: groundingIndex || undefined,
                    has_document_context: hasDocContext
                })
            });
            if (resp.ok) {
                setResult(await resp.json());
            } else {
                setError(await resp.text());
            }
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack tokens={{ childrenGap: 12 }} styles={{ root: { padding: 16, border: '1px solid #ddd', borderRadius: 8, background: '#fff' } }}>
            <Text variant="large" styles={{ root: { fontWeight: 600 } }}>Prompt Resolution Preview</Text>
            <Text variant="small" styles={{ root: { color: '#555' } }}>Simulate how the active system prompt is chosen for a question, grounding index, and context presence.</Text>
            <TextField label="Question" value={question} onChange={(_,v)=>setQuestion(v||'')} />
            <TextField label="Grounding Index (optional)" value={groundingIndex} onChange={(_,v)=>setGroundingIndex(v||'')} placeholder="policies-index" />
            <Checkbox label="Simulate document context present" checked={hasDocContext} onChange={(_,c)=>setHasDocContext(!!c)} />
            <Stack horizontal tokens={{ childrenGap: 8 }}>
                <PrimaryButton text="Preview Prompt" onClick={runPreview} disabled={loading} />
                {loading && <Spinner size={SpinnerSize.small} />}
            </Stack>
            {error && <MessageBar messageBarType={MessageBarType.error} onDismiss={()=>setError(null)}>{error}</MessageBar>}
            {result && (
                <Stack tokens={{ childrenGap: 8 }}>
                    <Text><strong>Source:</strong> {result.prompt_source}</Text>
                    {result.strict_grounding && <Text><strong>Strict Grounding:</strong> Enabled</Text>}
                    <Text><strong>Decision Trace:</strong></Text>
                    <ul style={{ marginTop: 4 }}>
                        {result.decision_trace?.map((d:string,i:number)=>(<li key={i}>{d}</li>))}
                    </ul>
                    <Text><strong>Applied Prompt:</strong></Text>
                    <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, background: '#f6f8fa', padding: 12, border: '1px solid #eee', borderRadius: 4 }}>
                        {result.applied_prompt}
                    </div>
                </Stack>
            )}
        </Stack>
    );
};
